import _ from 'lodash';
import loglevel from 'loglevel';
import { EasyCallControlFactory, init as hidInit, RequestedBrowserTransport } from '@gnaudio/jabra-js';
import nsToken from '@netsapiens/netsapiens-js/dist/token';

import i18n from 'i18next'; //eslint-disable-line
import Cookies from 'js-cookie';
import store from '../state/store';
import bugsnagClient from '../services/bugsnag/bugsnag';
import { updateElementSinkId } from './audio';
import { setShowHidConsent } from '../state/hidConsent/hidConsentSlice';
import { selectAppName, selectUserId } from '../state/configs/configsSlice';
import { YealinkManager, YealinkCallControlFactory } from './yealink/yealinkManager';
import { yealink, addDevice as addYealinkDevice } from './yealink/yealinkSDK';
import {
  selectActiveCall,
  selectActiveCount,
  selectCallSessions,
  setActiveCallId,
} from '../state/callSessions/callSessionsSlice';
import {
  selectHidDevice,
  selectHidDevices,
  selectYealinkDevice,
  selectYealinkDevices,
  selectHeadsetType,
  selectInputDevices,
  selectOutputDevices,
  setHidDevice,
  setHidDevices,
  setYealinkDevice,
  setYealinkDevices,
  setHeadsetType,
  setInputDevice,
  setInputDevices,
  setOutputDevice,
  setOutputDeviceRinger,
  setOutputDevices,
} from '../state/userMedia/userMediaSlice';
import { setSnackbar } from '../state/snackbar/snackbarSlice';
import { INFO, ONBOARD_COMPLETE, ONBOARD_SETUP } from '../constants';
import { acceptCallAction } from '../sagas/ua/acceptCall/action';
import { rejectCallAction } from '../sagas/ua/rejectCall/action';

let hidState = {
  pairing: false,
  initOngoing: false,
  isInitialLoad: true, // Track initial load to prevent premature consent dialogs
  incomingCallSessions: [],
  answeredCallSessions: [],
  onGoingCalls: 0,
  mergeCallId: null,
};

// Global instances for headset managers
let yealinkManager = null;
let yealinkCCFactory = null;
let ccFactory = null;
let activeJabraCallControl = null; // Store active Jabra device to avoid Redux serialization issues
let activeYealinkCallControl = null; // Store active Yealink device to avoid Redux serialization issues
const jabraCallControls = []; // Store all Jabra call control objects at module level to preserve methods

export const getHidState = () => hidState;
export const updateHidState = (state) => {
  hidState = { ...hidState, ...state };
};
export const getActiveJabraCallControl = () => activeJabraCallControl;
export const getActiveYealinkCallControl = () => activeYealinkCallControl;

export const getYealinkFactory = () => yealinkCCFactory;
export const addIncomingCall = (sessionId) => {
  debugger; //eslint-disable-line
  hidState.incomingCallSessions.push(sessionId);
};
export const removeIncomingCall = (sessionId) => {
  hidState.incomingCallSessions = hidState.incomingCallSessions.filter((id) => id !== sessionId);
};
export const addAnsweredCall = (sessionId) => {
  // Only add if not already in the array (prevent duplicates)
  if (!hidState.answeredCallSessions.includes(sessionId)) {
    hidState.answeredCallSessions.push(sessionId);
    return true; // Returns true if added
  }
  return false; // Returns false if already exists
};
export const removeAnsweredCall = (sessionId) => {
  hidState.answeredCallSessions = hidState.answeredCallSessions.filter((id) => id !== sessionId);
};
export const isCallAnswered = (sessionId) => hidState.answeredCallSessions.includes(sessionId);

const MODULE_NAME = 'user-media/devices';
const logger = loglevel.getLogger(MODULE_NAME);
logger.setLevel('info');

/**
 * Get the name from a call control device
 * @param callControl
 * @returns {string}
 */
export const getName = (callControl) => callControl?.name || callControl?.device?.name || '';

/**
 * Get Jabra call control by name from module-level array
 * @param {string} name - Device name
 * @returns {object|undefined} Call control object with methods intact
 */
export const getJabraCallControlByName = (name) => jabraCallControls.find((cc) => getName(cc) === name);

/**
 * Returns all available devices
 * @returns {Promise<MediaDeviceInfo[]>}
 */
export const getMediaDevices = () => navigator.mediaDevices.enumerateDevices();

/**
 * Returns a devices audio stream
 * @param deviceId
 * @returns {Promise<MediaStream>}
 */
export const getAudioStream = (deviceId) => navigator.mediaDevices.getUserMedia({
  audio: { deviceId },
  video: false,
});

export const getMozillaAudioOutput = () => navigator.mediaDevices.selectAudioOutput();

/**
 * Stop a device stream
 * @param stream
 */
export const stopStream = (stream) => {
  if (stream && stream.getTracks()) {
    stream.getTracks().forEach((track) => {
      track.stop();
    });
  }
};

/**
 * Filters the device list for audio input devices
 * @param devices
 * @returns {*}
 */
export const getFilteredAudioInput = (devices) => _.filter(
  devices,
  (device) => device.deviceId !== 'default' && device.kind === 'audioinput',
);

/**
 * Returns the last selected user input device
 * if it's still available or selects the browsers default device
 * @param appName
 * @param user
 * @param devices
 * @returns {*|null}
 */
export const getDefaultAudioInput = (appName, user, devices) => {
  debugger; //eslint-disable-line
  const previouslySelected = _.get(
    _.filter(devices, { deviceId: localStorage.getItem(`${appName}-${user}_inputAudioDevice`), kind: 'audioinput' }),
    '0',
  );
  const defaultDevice = _.get(_.filter(devices, { deviceId: 'default', kind: 'audioinput' }), '0');
  return previouslySelected || defaultDevice || _.get(getFilteredAudioInput(devices), '0') || null;
};

/**
 * Filters the device list for audio output devices
 * @param devices
 * @returns {*}
 */
export const getFilteredAudioOutput = (devices) => _.filter(
  devices,
  (device) => device.deviceId !== 'default' && device.kind === 'audiooutput',
);

/**
 * Returns the last selected user output device
 * if it's still available or selects the browsers default device
 * @param appName
 * @param user
 * @param devices
 * @returns {*|null}
 */
export const getDefaultAudioOutput = (appName, user, devices) => {
  const previouslySelected = _.get(
    _.filter(devices, { deviceId: localStorage.getItem(`${appName}-${user}_outputAudioDevice`), kind: 'audiooutput' }),
    '0',
  );
  const defaultDevice = _.get(_.filter(devices, { deviceId: 'default', kind: 'audiooutput' }), '0');
  return previouslySelected || defaultDevice || _.get(getFilteredAudioOutput(devices), '0') || null;
};

export const getDefaultAudioOutputRinger = (appName, user, devices) => {
  const previouslySelected = _.get(
    _.filter(
      devices,
      {
        deviceId: localStorage.getItem(`${appName}-${user}_outputAudioDeviceRinger`),
        kind: 'audiooutput',
      },
    ),
    '0',
  );
  const defaultDevice = _.get(_.filter(devices, { deviceId: 'default', kind: 'audiooutput' }), '0');
  return previouslySelected || defaultDevice || _.get(getFilteredAudioOutput(devices), '0') || null;
};

/**
 * Returns the browser device label
 * @param deviceId
 * @returns {Promise<*>}
 */
export const getDeviceLabel = async (deviceId) => {
  const devices = await getMediaDevices();
  return _.find(devices, { deviceId })?.label;
};

/**
 * The browser device and the hid device don't share the same id
 * the two entities need to be matched by label
 * @param deviceLabel
 * @returns {*}
 */
export const getHidDeviceByLabel = (deviceLabel) => {
  const hidDevices = selectHidDevices(store.getState());
  return _.find(hidDevices, (callControlDevice) => deviceLabel?.toLowerCase().includes(getName(callControlDevice).toLowerCase()));
};

/**
 * Get Yealink device by label
 * @param deviceLabel
 * @returns {*}
 */
export const getYealinkDeviceByLabel = (deviceLabel) => {
  const yealinkDevices = selectYealinkDevices(store.getState());
  logger.info(
    `${MODULE_NAME} - getYealinkDeviceByLabel - searching for: "${deviceLabel}", available devices:`,
    yealinkDevices.map((d) => getName(d)),
  );
  const found = _.find(
    yealinkDevices,
    (callControlDevice) => deviceLabel?.toLowerCase().includes(getName(callControlDevice).toLowerCase()),
  );
  logger.info(`${MODULE_NAME} - getYealinkDeviceByLabel - found:`, !!found, found ? getName(found) : 'none');
  return found;
};

/**
 * Get any headset device (Jabra or Yealink) by label
 * @param deviceLabel
 * @returns {*}
 */
export const getHeadsetDeviceByLabel = (deviceLabel) => {
  // Try Jabra first
  let device = getHidDeviceByLabel(deviceLabel);
  if (device) return { device, type: 'jabra' };

  // Try Yealink
  device = getYealinkDeviceByLabel(deviceLabel);
  debugger; //eslint-disable-line
  if (device) return { device, type: 'yealink' };

  return null;
};

/**
 * Updates Yealink headset state when SIP call changes
 * Should be called when mute/unmute/hold/unhold is performed from UI
 */
export const syncYealinkWithCall = (activeCall) => {
  const headsetType = selectHeadsetType(store.getState());
  if (headsetType !== 'yealink') {
    return;
  }

  try {
    // Handle case when no active call (clear headset state)
    if (!activeCall) {
      yealink.onHook();
      yealink.updateCallStatus({
        callActive: false,
        muteStatus: false,
        holdStatus: false,
      });
      return;
    }

    const isMuted = activeCall.isMuted || false;
    const isOnHold = activeCall.isOnHold || false;
    const currentStatus = yealink.getCallStatus();

    // Update internal SDK call status first
    yealink.updateCallStatus({
      // callActive: true,
      muteStatus: isMuted,
      holdStatus: isOnHold,
    });

    // Sync mute state to headset LEDs
    if (isMuted !== currentStatus.muteStatus) {
      if (isMuted) {
        logger.info(`${MODULE_NAME} - syncYealinkWithCall - Syncing MUTE to headset`);
        yealink.mute(true);
      } else {
        logger.info(`${MODULE_NAME} - syncYealinkWithCall - Syncing UNMUTE to headset`);
        yealink.unmute(true);
      }
    }

    // Sync hold state to headset LEDs
    if (isOnHold !== currentStatus.holdStatus) {
      if (isOnHold) {
        logger.info(`${MODULE_NAME} - syncYealinkWithCall - Syncing HOLD to headset`);
        yealink.hold(true);
      } else {
        logger.info(`${MODULE_NAME} - syncYealinkWithCall - Syncing RESUME to headset`);
        yealink.resume(true);
      }
    }
  } catch (error) {
    logger.error(`${MODULE_NAME} - syncYealinkWithCall error:`, error);
  }
};

/**
 * Replaces audio track for active SIP calls when switching input devices
 * @param {string} newInputDeviceId - The new input device ID to use
 */
export const replaceAudioTrackForActiveCalls = async (newInputDeviceId) => {
  try {
    const callSessions = selectCallSessions(store.getState());
    const activeCalls = callSessions.filter((session) => (
      session.status === 'established' 
      || session.status === 'outboundProgressing' 
      || session.status === 'inboundProgressing'
    ));

    if (activeCalls.length === 0) {
      logger.info(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - No active calls to update`);
      return;
    }

    logger.info(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - Updating audio tracks for ${activeCalls.length} active calls`);

    // Get new audio stream with exact device constraint
    const newStream = await getAudioStream(newInputDeviceId);
    const newAudioTrack = newStream.getAudioTracks()[0];

    if (!newAudioTrack) {
      throw new Error('No audio track found in new stream');
    }

    // Replace audio track for each active call
    const replacePromises = activeCalls.map(async (callSession) => {
      try {
        const sessionDescriptionHandler = callSession.session?.sessionDescriptionHandler;
        const peerConnection = sessionDescriptionHandler?.peerConnection;

        if (!peerConnection) {
          logger.warn(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - No peer connection for session ${callSession.id}`);
          return;
        }

        // Find audio sender and replace track
        const audioSender = peerConnection.getSenders().find((sender) => sender.track && sender.track.kind === 'audio');

        if (audioSender) {
          await audioSender.replaceTrack(newAudioTrack.clone());
          logger.info(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - Successfully replaced audio track for session ${callSession.id}`);
        } else {
          logger.warn(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - No audio sender found for session ${callSession.id}`);
        }
      } catch (error) {
        logger.error(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - Error replacing track for session ${callSession.id}:`, error);
      }
    });

    await Promise.all(replacePromises);

    // Stop the original tracks in the temporary stream
    newStream.getTracks().forEach((track) => track.stop());

    logger.info(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - Audio track replacement completed`);
  } catch (error) {
    logger.error(`${MODULE_NAME} - replaceAudioTrackForActiveCalls - Error:`, error);
    throw error;
  }
};

/**
 * Adds a call to the active headset call control
 */
export const addCall = async () => {
  debugger; //eslint-disable-line
  const headsetType = selectHeadsetType(store.getState());

  logger.info(`${MODULE_NAME} - addCall to ${headsetType}`);
  console.log('addCall - headsetType:', headsetType); //eslint-disable-line

  try {
    if (headsetType === 'yealink') {
      // For Yealink, ensure device is opened before calling offHook
      logger.info(`${MODULE_NAME} - addCall - ensuring Yealink device is opened`);

      try {
        const isOpened = await yealink.openDevice();
        if (!isOpened) {
          logger.warn(`${MODULE_NAME} - addCall - Yealink device failed to open`);
          return;
        }
      } catch (error) {
        logger.error(`${MODULE_NAME} - addCall - Error opening Yealink device:`, error);
        return;
      }

      logger.info(`${MODULE_NAME} - addCall - starting Yealink call via SDK`);

      // Reset initOngoing flag BEFORE calling offHook so button presses can end the call
      // This must be done before handleStartCall triggers the subscription
      hidState.initOngoing = false;
      console.log('addCall - reset hidState.initOngoing to false'); //eslint-disable-line

      const success = yealink.offHook();
      if (success) {
        logger.info(`${MODULE_NAME} - addCall - Yealink call started successfully`);

        // Get the Yealink call control from module level (not Redux) to avoid serialization issues
        const yealinkCallControl = getActiveYealinkCallControl();
        console.log('addCall - yealinkCallControl from module:', yealinkCallControl); //eslint-disable-line
        console.log('addCall - yealinkCallControl type:', typeof yealinkCallControl); //eslint-disable-line
        console.log('addCall - has handleStartCall:', yealinkCallControl ? typeof yealinkCallControl.handleStartCall : 'no device'); //eslint-disable-line
        if (yealinkCallControl && typeof yealinkCallControl.handleStartCall === 'function') {
          logger.info(`${MODULE_NAME} - addCall - triggering handleStartCall on Yealink call control`);
          yealinkCallControl.handleStartCall();
        } else {
          logger.warn(`${MODULE_NAME} - addCall - Yealink call control not available or missing handleStartCall`);
          logger.warn(`${MODULE_NAME} - addCall - yealinkCallControl value:`, yealinkCallControl);
        }

        // Sync the current call state to headset after a brief delay
        setTimeout(() => {
          const activeCall = selectActiveCall(store.getState());
          if (activeCall) {
            syncYealinkWithCall(activeCall);
          }
        }, 100);
      } else {
        logger.warn(`${MODULE_NAME} - addCall - Yealink call start returned false`);
      }
    } else if (headsetType === 'jabra') {
      // For Jabra, use the module-level variable to avoid Redux serialization
      logger.info(`${MODULE_NAME} - addCall - starting Jabra call via SDK`);
      const jabraCallControl = getActiveJabraCallControl();
      console.log('addCall - jabraCallControl from module:', jabraCallControl); //eslint-disable-line
      console.log('addCall - jabraCallControl type:', typeof jabraCallControl); //eslint-disable-line
      console.log('addCall - has startCall:', jabraCallControl ? typeof jabraCallControl.startCall : 'no device'); //eslint-disable-line

      if (jabraCallControl && typeof jabraCallControl.startCall === 'function') {
        jabraCallControl.startCall().then(() => {
          logger.info(`${MODULE_NAME} - addCall - Jabra call started successfully`);
        }).catch((error) => {
          logger.error(`${MODULE_NAME} - addCall - Jabra startCall error:`, error);
        });
      } else {
        logger.warn(`${MODULE_NAME} - addCall - Jabra startCall method not available on device`);
        logger.warn(`${MODULE_NAME} - addCall - jabraCallControl value:`, jabraCallControl);
      }
    } else {
      logger.info(`${MODULE_NAME} - addCall - no active headset or unsupported type: ${headsetType}`);
    }
  } catch (e) {
    console.error(e); //eslint-disable-line
    logger.error(`${MODULE_NAME} - addCall - failed to start device call:`, e);
  }
};

/**
 * Listens for hid device signals
 * and updates an active call session if there is one
 * @param callControl
 */
const hidSubscribe = (callControl) => {
  debugger; //eslint-disable-line
  logger.info(`${MODULE_NAME} - hid subscribe call control`, callControl);
  console.log('hidSubscribe - callControl:', callControl); //eslint-disable-line
  console.log('hidSubscribe - muteState:', callControl?.muteState); //eslint-disable-line
  console.log('hidSubscribe - holdState:', callControl?.holdState); //eslint-disable-line
  console.log('hidSubscribe - ongoingCalls:', callControl?.ongoingCalls); //eslint-disable-line
  console.log('hidSubscribe - swapRequest:', callControl?.swapRequest); //eslint-disable-line

  if (!callControl) {
    logger.error(`${MODULE_NAME} - hidSubscribe - callControl is null or undefined`);
    return;
  }

  if (!callControl.muteState || typeof callControl.muteState.subscribe !== 'function') {
    logger.error(`${MODULE_NAME} - hidSubscribe - invalid callControl or missing muteState.subscribe:`, callControl);
    console.error('hidSubscribe - missing muteState or subscribe method'); //eslint-disable-line
    return;
  }

  console.log('hidSubscribe - subscribing to muteState'); //eslint-disable-line
  callControl.muteState.subscribe((s) => {
    debugger; //eslint-disable-line
    console.log('hidSubscribe - muteState changed:', s); //eslint-disable-line
    logger.info(`${MODULE_NAME} - hid mute state`, s);

    const activeCall = selectActiveCall(store.getState());

    logger.info(`${MODULE_NAME} - hid mute state - active call`, activeCall);
    logger.info(`${MODULE_NAME} - hid mute state - active call isMuted`, activeCall?.isMuted);

    if (activeCall) {
      debugger; //eslint-disable-line
      if (s === 'muted' && !activeCall.isMuted) {
        logger.info(`${MODULE_NAME} - hid - set active call as muted`);
        activeCall.mute(false); // false prevents loop
      } else if (s === 'unmuted' && activeCall.isMuted) {
        logger.info(`${MODULE_NAME} - hid - set active call as unmuted`);
        activeCall.unmute(false); // false prevents loop
      }
    }
  });

  /**
   * Emits the hold state of the device whenever that state changes.
   * This can happen due to interaction with the device
   * (the hold command can differ between devices,
   * but is often triggered by long-pressing the start/end-call button) or
   * due to interaction with your softphone (e.g. pressing a hold button in the GUI).
   * true is emitted when the device is on hold,
   * false is emitted when the device is not on hold e.g. resumed.
   */
  console.log('hidSubscribe - subscribing to holdState'); //eslint-disable-line
  callControl.holdState.subscribe((s) => {
    console.log('hidSubscribe - holdState changed:', s); //eslint-disable-line
    logger.info(`${MODULE_NAME} - hid hold state`, s);

    const activeCall = selectActiveCall(store.getState());
    logger.info(`${MODULE_NAME} - hid hold state - active call`, activeCall);
    logger.info(`${MODULE_NAME} - hid hold state - active call isOnHold`, activeCall?.isOnHold);

    if (activeCall) {
      if (s === 'on-hold' && !activeCall.isOnHold) {
        logger.info(`${MODULE_NAME} - hid - set active call on hold`);
        activeCall.hold(false); // false prevents loop
      } else if (s === 'not-on-hold' && activeCall.isOnHold) {
        logger.info(`${MODULE_NAME} - hid - set active call as unhold`);
        activeCall.unhold(false); // false prevents loop
      }
    }
  });

  /**
   * Emits whenever swap is triggered.
   * This can happen due to interaction with the device
   * (most often the same button that triggers hold) or
   * due to interaction with your softphone
   * (e.g. pressing a swap button in the GUI).
   * The observable does not emit any value,
   * and it does not keep track of what call was swapped to or
   * from - this should be handled by the softphone application.
   * The most common implementation pattern would be to
   * maintain a list of ongoing calls in the softphone application.
   * Then, whenever swap is triggered, the active call is moved to the next call in line.
   */
  console.log('hidSubscribe - subscribing to swapRequest'); //eslint-disable-line
  callControl.swapRequest.subscribe(() => {
    console.log('hidSubscribe - swapRequest triggered'); //eslint-disable-line
    const activeCall = selectActiveCall(store.getState());
    const callSessions = selectCallSessions(store.getState());

    // if there is an active call and has more than one call session
    logger.info(`${MODULE_NAME} - hid swap call`, activeCall, callSessions);
    if (activeCall && callSessions) {
      logger.info(`${MODULE_NAME} - hid swap call - hold active`, activeCall);
      activeCall.hold(false);
      for (let i = 0; i < callSessions.length; i += 1) {
        if (activeCall.id !== callSessions[i].id) {
          logger.info(`${MODULE_NAME} - hid swap - call unhold and set to active`, callSessions[i]);
          callSessions[i].unhold(false);
          store.dispatch(setActiveCallId(callSessions[i].id));
          break;
        }
      }
    }
  });

  /**
   * Emits the number of calls currently in progress.
   * 0 means that the device is idle with no calls in progress 1 or
   * more signifies the number of calls currently active
   * Starting a new call - or accepting an incoming call - will increment this counter.
   * Similarly, ending a call will decrement the count until it reaches 0.
   * If you wish to handle multiple calls scenarios make sure to keep your
   * application's list of ongoing calls in sync with this count.
   * If you only want to handle one active call at a time, treat this value as an on-off toggle.
   * Optionally throw an error if the count increases to more than 1,
   * which would mean something went wrong in the application logic.
   */
  console.log('hidSubscribe - subscribing to ongoingCalls'); //eslint-disable-line
  callControl.ongoingCalls.subscribe((s) => {
    console.log('hidSubscribe - ongoingCalls changed:', s); //eslint-disable-line
    const activeCall = selectActiveCall(store.getState());
    const callSessions = selectCallSessions(store.getState());

    updateHidState({ onGoingCalls: s });

    if (hidState.onGoingCalls === 1
      && hidState.incomingCallSessions.length === 1
      && hidState.answeredCallSessions.length === 0
    ) {
      const callSession = callSessions.find((session) => session.id === hidState.incomingCallSessions[0]);
      if (callSession) {
        removeIncomingCall(callSession.id);
        addAnsweredCall(callSession.id);
        store.dispatch(acceptCallAction({ call: callSession }));
      }
    } else if (hidState.onGoingCalls === 1
      && hidState.incomingCallSessions.length === 2
      && hidState.answeredCallSessions.length === 0
    ) {
      const callSession = callSessions.find((session) => session.id === hidState.incomingCallSessions[0]);
      if (callSession) {
        removeIncomingCall(callSession.id);
        addAnsweredCall(callSession.id);
        store.dispatch(acceptCallAction({ call: callSession }));
      }
    }

    if (!hidState.initOngoing && activeCall && s < hidState.answeredCallSessions.length) {
      removeAnsweredCall(activeCall.id);

      // check if the call has already been established
      // fix for outbound calls being cancelled
      if (activeCall.status === 'trying'
        || activeCall.status === 'inboundProgressing'
        || activeCall.status === 'outboundProgressing'
      ) {
        activeCall.cancel(false);
      } else {
        activeCall.bye();
      }
    }

    hidState.initOngoing = false;

    if (s === 0 && hidState.mergeCallId) {
      store.dispatch(setActiveCallId(hidState.mergeCallId));
      addAnsweredCall(hidState.mergeCallId);
      addCall();
      updateHidState({ mergeCallId: null });
    }
  });

  console.log('hidSubscribe - subscribing to onDisconnect'); //eslint-disable-line
  callControl.onDisconnect.subscribe((s) => {
    console.log('hidSubscribe - onDisconnect triggered:', s); //eslint-disable-line
    logger.info(`${MODULE_NAME} - hid disconnect`, s);
  });

  console.log('hidSubscribe - all subscriptions completed'); //eslint-disable-line
};

/**
 * Unsubscribes call control listeners and ends calls if there are any call sessions
 * @param callControl
 */
const hidUnsubscribe = (callControl) => {
  logger.info(`${MODULE_NAME} - hid unsubscribe`, callControl);
  try {
    // Check if each property exists and has an unsubscribe method before calling
    if (callControl?.muteState && typeof callControl.muteState.unsubscribe === 'function') {
      callControl.muteState.unsubscribe(_.noop);
    }
    if (callControl?.holdState && typeof callControl.holdState.unsubscribe === 'function') {
      callControl.holdState.unsubscribe(_.noop);
    }
    if (callControl?.swapRequest && typeof callControl.swapRequest.unsubscribe === 'function') {
      callControl.swapRequest.unsubscribe(_.noop);
    }
    if (callControl?.ongoingCalls && typeof callControl.ongoingCalls.unsubscribe === 'function') {
      callControl.ongoingCalls.unsubscribe(_.noop);
    }
    if (callControl?.onDisconnect && typeof callControl.onDisconnect.unsubscribe === 'function') {
      callControl.onDisconnect.unsubscribe(_.noop);
    }
  } catch (error) {
    logger.error(`${MODULE_NAME} - hid unsubscribe error:`, error);
  }
};

export const setCallControl = async (callControl, showSnackbar = true) => {
  logger.info(`${MODULE_NAME} - setCallControl`, callControl);

  // Check if we're switching from Yealink to Jabra
  const prevYealinkDevice = selectYealinkDevice(store.getState());
  const headsetType = selectHeadsetType(store.getState());
  if (headsetType === 'yealink' && prevYealinkDevice) {
    logger.info(`${MODULE_NAME} - setCallControl - switching from Yealink to Jabra`);
    // DON'T unsubscribe - keep observables alive so we can switch back
    // try {
    //   if (prevYealinkDevice?.muteState && typeof prevYealinkDevice.muteState.unsubscribe === 'function') {
    //     prevYealinkDevice.muteState.unsubscribe(_.noop);
    //   }
    //   if (prevYealinkDevice?.holdState && typeof prevYealinkDevice.holdState.unsubscribe === 'function') {
    //     prevYealinkDevice.holdState.unsubscribe(_.noop);
    //   }
    //   if (prevYealinkDevice?.ongoingCalls && typeof prevYealinkDevice.ongoingCalls.unsubscribe === 'function') {
    //     prevYealinkDevice.ongoingCalls.unsubscribe(_.noop);
    //   }
    // } catch (error) {
    //   logger.error(`${MODULE_NAME} - setCallControl - Yealink unsubscribe error:`, error);
    // }

    const count = selectActiveCount(store.getState());

    // End calls on Yealink device
    if (count > 0) {
      logger.info(`${MODULE_NAME} - setCallControl - ending ${count} calls on Yealink`);
      _.times(count, () => yealink.onHook());
    }

    // DON'T clear Yealink from Redux - keep it so we can switch back
    // store.dispatch(setYealinkDevice(null));
  }

  const prevCallControl = selectHidDevice(store.getState());

  // Only unsubscribe if it's a different device (check by device name to avoid closing same device)
  const isSameDevice = prevCallControl && callControl
    && getName(prevCallControl) === getName(callControl);

  if (prevCallControl && !isSameDevice) {
    logger.info(`${MODULE_NAME} - setCallControl - switching from previous Jabra call control`);
    // DON'T unsubscribe - keep observables alive so we can switch back
    // hidUnsubscribe(prevCallControl);
    const count = selectActiveCount(store.getState());
    logger.info(`${MODULE_NAME} - setCallControlByDeviceId - remove previous calls`, count);

    // Only try to end calls if there are active calls and the device has endCall method
    if (count > 0 && prevCallControl.endCall && typeof prevCallControl.endCall === 'function') {
      try {
        _.times(count, () => {
          try {
            prevCallControl.endCall();
          } catch (endCallError) {
            logger.warn(`${MODULE_NAME} - setCallControl - Error ending individual call:`, endCallError);
          }
        });
      } catch (error) {
        logger.error(`${MODULE_NAME} - setCallControl - Error during call cleanup:`, error);
      }
    } else if (count === 0) {
      logger.info(`${MODULE_NAME} - setCallControl - No active calls to end during device switch`);
    } else {
      logger.warn(`${MODULE_NAME} - setCallControl - Previous device has no endCall method, skipping call cleanup`);
    }
  }

  if (callControl) {
    logger.info(`${MODULE_NAME} - setCallControlByDeviceId - switch to new call control`, callControl);

    // Look up the proper call control from module-level array (has methods)
    // instead of using the serialized one from Redux (no methods)
    console.log('setCallControl - callControl.device:', callControl.device); //eslint-disable-line
    console.log('setCallControl - jabraCallControls.length:', jabraCallControls.length); //eslint-disable-line
    let properCallControl = getJabraCallControlByName(getName(callControl));
    console.log('setCallControl - properCallControl from lookup:', properCallControl); //eslint-disable-line
    if (properCallControl) {
      activeJabraCallControl = properCallControl;
      logger.info(`${MODULE_NAME} - setCallControl - found proper call control from module array`);
    } else if (callControl.device) {
      // This is a serialized call control. Try to recreate it from the base device.
      logger.warn(`${MODULE_NAME} - setCallControl - call control not in module array, attempting to recreate from device`);
      try {
        if (ccFactory && ccFactory.supportsEasyCallControl(callControl.device)) {
          properCallControl = await ccFactory.createMultiCallControl(callControl.device);
          jabraCallControls.push(properCallControl);
          activeJabraCallControl = properCallControl;
          logger.info(`${MODULE_NAME} - setCallControl - recreated call control and added to module array`);
        } else {
          logger.error(`${MODULE_NAME} - setCallControl - ccFactory not available or device not supported`);
          activeJabraCallControl = callControl;
        }
      } catch (error) {
        logger.error(`${MODULE_NAME} - setCallControl - failed to recreate call control:`, error);
        activeJabraCallControl = callControl;
      }
    } else {
      // Fallback to the passed-in callControl if not found in array
      logger.warn(`${MODULE_NAME} - setCallControl - could not find call control in module array, using passed-in control`);
      activeJabraCallControl = callControl;
    }

    console.log('setCallControl - activeJabraCallControl:', activeJabraCallControl); //eslint-disable-line
    console.log('setCallControl - has startCall:', typeof activeJabraCallControl?.startCall); //eslint-disable-line
    console.log('setCallControl - has endCall:', typeof activeJabraCallControl?.endCall); //eslint-disable-line
    logger.info(`${MODULE_NAME} - setCallControl - stored Jabra device at module level`);

    const token = nsToken.getDecoded();
    const appName = selectAppName(store.getState());
    const firstLoginSettings = localStorage.getItem(`${appName}-${token.user}_onboarding`);
    const isOnboardingSettings = firstLoginSettings === ONBOARD_SETUP || firstLoginSettings === null;
    if (showSnackbar) {
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - headset paired`);
      store.dispatch(setSnackbar({
        type: INFO,
        open: true,
        message: i18n.t('HEADSET_PAIRED').replace('%s', getName(callControl)),
        ...(!window.location.pathname.includes('/settings') && {
          action: isOnboardingSettings ? null : {
            label: i18n.t('Settings'),
            link: '/settings',
          },
        }),
        duration: 3000,
      }));
    }
    
    const userId = selectUserId(store.getState());
    localStorage.setItem(`${appName}-${userId}_hidDevice`, getName(callControl));

    store.dispatch(setHidDevice(callControl));
    console.log("setting hid device to Jabra");  //eslint-disable-line
    store.dispatch(setHeadsetType('jabra'));
    localStorage.setItem(`${appName}-${userId}_headsetType`, 'jabra');
    hidState.initOngoing = true;
    hidSubscribe(callControl);
    const count = selectActiveCount(store.getState());
    logger.info(`${MODULE_NAME} - setCallControl - start calls`, count);

    // Start calls on the Jabra headset if there are active calls
    if (count > 0 && activeJabraCallControl && typeof activeJabraCallControl.startCall === 'function') {
      logger.info(`${MODULE_NAME} - setCallControl - signaling ${count} active calls to Jabra`);
      const callSessions = selectCallSessions(store.getState());

      // Add all active call sessions to answeredCallSessions for tracking
      callSessions.forEach((session) => {
        if (session.status === 'accepted' && !hidState.answeredCallSessions.includes(session.id)) {
          addAnsweredCall(session.id);
          logger.info(`${MODULE_NAME} - setCallControl - added call ${session.id} to answeredCallSessions`);
        }
      });

      _.times(count, () => {
        activeJabraCallControl.startCall().then(() => {
          logger.info(`${MODULE_NAME} - setCallControl - Jabra call started successfully`);
          // Reset initOngoing after first call is started
          if (hidState.initOngoing) {
            hidState.initOngoing = false;
            console.log('setCallControl - reset hidState.initOngoing to false after starting call'); //eslint-disable-line
          }
        }).catch((error) => {
          logger.error(`${MODULE_NAME} - setCallControl - Jabra startCall error:`, error);
        });
      });
    } else if (count === 0) {
      logger.info(`${MODULE_NAME} - setCallControl - No active calls to start on Jabra during device switch`);
    }
  } else {
    logger.info(`${MODULE_NAME} - setCallControlByDeviceId - no matching hid device`);
    activeJabraCallControl = null;
    store.dispatch(setHidDevice(null));
  }
};

/**
 * Subscribe to Yealink call control events
 * Similar to hidSubscribe but for Yealink devices
 */
const yealinkSubscribe = (callControl) => {
  logger.info(`${MODULE_NAME} - yealink subscribe call control`, callControl);

  // Defensive check for required observable properties
  if (!callControl || !callControl.muteState || typeof callControl.muteState.subscribe !== 'function') {
    logger.error(`${MODULE_NAME} - yealink subscribe - invalid callControl or missing muteState:`, callControl);
    return;
  }

  callControl.muteState.subscribe((s) => {
    logger.info(`${MODULE_NAME} - yealink mute state`, s);

    const activeCall = selectActiveCall(store.getState());
    logger.info(`${MODULE_NAME} - yealink mute state - active call`, activeCall);
    logger.info(`${MODULE_NAME} - yealink mute state - active call isMuted`, activeCall?.isMuted);

    if (activeCall) {
      if (s === 'muted' && !activeCall.isMuted) {
        logger.info(`${MODULE_NAME} - yealink - set active call as muted`);
        activeCall.mute();
      } else if (s === 'unmuted' && activeCall.isMuted) {
        logger.info(`${MODULE_NAME} - yealink - set active call as unmuted`);
        activeCall.unmute();
      }
    }
  });

  // Defensive check for holdState
  if (!callControl.holdState || typeof callControl.holdState.subscribe !== 'function') {
    logger.error(`${MODULE_NAME} - yealink subscribe - missing holdState:`, callControl);
    return;
  }

  callControl.holdState.subscribe((s) => {
    logger.info(`${MODULE_NAME} - yealink hold state`, s);

    const activeCall = selectActiveCall(store.getState());
    logger.info(`${MODULE_NAME} - yealink hold state - active call`, activeCall);
    logger.info(`${MODULE_NAME} - yealink hold state - active call isOnHold`, activeCall?.isOnHold);

    if (activeCall) {
      if (s === 'on-hold' && !activeCall.isOnHold) {
        logger.info(`${MODULE_NAME} - yealink - set active call on hold`);
        activeCall.hold();
      } else if (s === 'not-on-hold' && activeCall.isOnHold) {
        logger.info(`${MODULE_NAME} - yealink - set active call as unhold`);
        activeCall.unhold();
      }
    }
  });

  // Defensive check for ongoingCalls
  if (!callControl.ongoingCalls || typeof callControl.ongoingCalls.subscribe !== 'function') {
    logger.error(`${MODULE_NAME} - yealink subscribe - missing ongoingCalls:`, callControl);
    return;
  }

  callControl.ongoingCalls.subscribe((s) => {
    const activeCall = selectActiveCall(store.getState());
    const callSessions = selectCallSessions(store.getState());

    console.log('yealinkSubscribe - ongoingCalls changed:', s); //eslint-disable-line
    console.log('yealinkSubscribe - activeCall:', activeCall); //eslint-disable-line
    console.log('yealinkSubscribe - hidState.initOngoing:', hidState.initOngoing); //eslint-disable-line
    console.log('yealinkSubscribe - hidState.answeredCallSessions:', hidState.answeredCallSessions); //eslint-disable-line
    logger.info(`${MODULE_NAME} - yealink ongoing calls`, s, activeCall, callSessions);

    updateHidState({ onGoingCalls: s });

    // Handle call answer/reject logic similar to Jabra
    if (hidState.onGoingCalls === 1
      && hidState.incomingCallSessions.length === 1
      && hidState.answeredCallSessions.length === 0
    ) {
      const callSession = callSessions.find((session) => session.id === hidState.incomingCallSessions[0]);
      if (callSession) {
        removeIncomingCall(callSession.id);
        addAnsweredCall(callSession.id);
        store.dispatch(acceptCallAction({ call: callSession }));
      }
    }

    if (hidState.onGoingCalls === 0
      && hidState.incomingCallSessions.length === 1
      && hidState.answeredCallSessions.length === 0
    ) {
      const callSession = callSessions.find((session) => session.id === hidState.incomingCallSessions[0]);
      if (callSession) {
        removeIncomingCall(callSession.id);
        store.dispatch(rejectCallAction({ call: callSession }));
      }
    }

    // Handle call end logic
    console.log('yealinkSubscribe - checking call end logic'); //eslint-disable-line
    console.log('yealinkSubscribe - !hidState.initOngoing:', !hidState.initOngoing); //eslint-disable-line
    console.log('yealinkSubscribe - activeCall exists:', !!activeCall); //eslint-disable-line
    console.log('yealinkSubscribe - s < hidState.answeredCallSessions.length:', s, '<', hidState.answeredCallSessions.length, '=', s < hidState.answeredCallSessions.length); //eslint-disable-line
    if (!hidState.initOngoing && activeCall && s < hidState.answeredCallSessions.length) {
      console.log('yealinkSubscribe - ENDING CALL via button press'); //eslint-disable-line
      removeAnsweredCall(activeCall.id);

      // Check session state to determine whether to reject or hang up
      const sessionState = activeCall.session?.state;

      if (activeCall.status === 'trying'
        || activeCall.status === 'inboundProgressing'
        || activeCall.status === 'outboundProgressing'
      ) {
        console.log('yealinkSubscribe - calling cancel()'); //eslint-disable-line
        activeCall.cancel(false);
      } else if (sessionState === 'Initial' || sessionState === 'Early') {
        // For incoming calls that haven't been fully established, reject instead of bye
        console.log('yealinkSubscribe - session not established, calling reject()'); //eslint-disable-line
        activeCall.reject(false); // false = don't signal HID (we're already handling it)
      } else {
        console.log('yealinkSubscribe - calling bye()'); //eslint-disable-line
        activeCall.bye();
      }
    } else {
      console.log('yealinkSubscribe - NOT ending call, condition not met'); //eslint-disable-line
    }

    // Reset initOngoing flag to match Jabra behavior
    // This ensures the end button works properly after any call is established
    hidState.initOngoing = false;
  });
};

/**
 * Unsubscribe from Yealink call control events
 * Currently unused but kept for potential future use
 */
const yealinkUnsubscribe = (callControl) => { // eslint-disable-line no-unused-vars
  logger.info(`${MODULE_NAME} - yealink unsubscribe`, callControl);
  try {
    // Check if each property exists and has an unsubscribe method before calling
    if (callControl?.muteState && typeof callControl.muteState.unsubscribe === 'function') {
      callControl.muteState.unsubscribe(_.noop);
    }
    if (callControl?.holdState && typeof callControl.holdState.unsubscribe === 'function') {
      callControl.holdState.unsubscribe(_.noop);
    }
    if (callControl?.ongoingCalls && typeof callControl.ongoingCalls.unsubscribe === 'function') {
      callControl.ongoingCalls.unsubscribe(_.noop);
    }
  } catch (error) {
    logger.error(`${MODULE_NAME} - yealink unsubscribe error:`, error);
  }
};

/**
 * Set Yealink call control device
 */
export const setYealinkCallControl = (callControl, showSnackbar = true) => {
  console.log("setYealinkCallControl");  //eslint-disable-line
  logger.info(`${MODULE_NAME} - setYealinkCallControl`, callControl);

  // Validate callControl object structure
  if (callControl) {
    logger.info(`${MODULE_NAME} - setYealinkCallControl - callControl type:`, typeof callControl);
    logger.info(`${MODULE_NAME} - setYealinkCallControl - callControl properties:`, Object.keys(callControl));
    logger.info(`${MODULE_NAME} - setYealinkCallControl - has muteState:`, !!callControl.muteState);
    logger.info(`${MODULE_NAME} - setYealinkCallControl - has holdState:`, !!callControl.holdState);
    logger.info(`${MODULE_NAME} - setYealinkCallControl - has ongoingCalls:`, !!callControl.ongoingCalls);
  }

  // Check if we're switching from Jabra to Yealink
  const prevJabraDevice = selectHidDevice(store.getState());
  const headsetType = selectHeadsetType(store.getState());
  if (headsetType === 'jabra' && prevJabraDevice) {
    logger.info(`${MODULE_NAME} - setYealinkCallControl - switching from Jabra to Yealink`);
    // DON'T unsubscribe - keep observables alive so we can switch back
    // hidUnsubscribe(prevJabraDevice);
    const count = selectActiveCount(store.getState());

    // End calls on Jabra device
    if (count > 0 && prevJabraDevice.endCall && typeof prevJabraDevice.endCall === 'function') {
      try {
        _.times(count, () => {
          try {
            const result = prevJabraDevice.endCall();
            if (result && typeof result.catch === 'function') {
              result.catch(console.error);
            }
          } catch (endCallError) {
            logger.warn(`${MODULE_NAME} - setYealinkCallControl - Error ending Jabra call:`, endCallError);
          }
        });
      } catch (error) {
        logger.error(`${MODULE_NAME} - setYealinkCallControl - Error during Jabra cleanup:`, error);
      }
    }

    // DON'T clear Jabra from Redux or module level - keep it so we can switch back
    // store.dispatch(setHidDevice(null));
    // activeJabraCallControl = null;
  }

  const prevCallControl = selectYealinkDevice(store.getState());

  // Only unsubscribe if it's a different device (check by device name to avoid closing same device)
  const isSameDevice = prevCallControl && callControl
    && getName(prevCallControl) === getName(callControl);

  if (prevCallControl && !isSameDevice) {
    logger.info(`${MODULE_NAME} - setYealinkCallControl - switching from previous Yealink device`);
    // DON'T unsubscribe - keep observables alive so we can switch back
    // yealinkUnsubscribe(prevCallControl);
    const count = selectActiveCount(store.getState());
    logger.info(`${MODULE_NAME} - setYealinkCallControl - remove previous calls`, count);

    // Only try to end calls if there are active calls and the device has endCall method
    if (count > 0 && prevCallControl.endCall && typeof prevCallControl.endCall === 'function') {
      try {
        _.times(count, () => {
          try {
            prevCallControl.endCall();
          } catch (endCallError) {
            logger.warn(`${MODULE_NAME} - setYealinkCallControl - Error ending individual call:`, endCallError);
          }
        });
      } catch (error) {
        logger.error(`${MODULE_NAME} - setYealinkCallControl - Error during call cleanup:`, error);
      }
    } else if (count === 0) {
      logger.info(`${MODULE_NAME} - setYealinkCallControl - No active calls to end during device switch`);
    } else {
      logger.warn(`${MODULE_NAME} - setYealinkCallControl - Previous device has no endCall method, skipping call cleanup`);
    }
  }
  if (callControl) {
    logger.info(`${MODULE_NAME} - setYealinkCallControl - switch to new call control`, callControl);

    // Store Yealink device at module level to avoid Redux serialization issues
    activeYealinkCallControl = callControl;
    console.log('setYealinkCallControl - activeYealinkCallControl set:', activeYealinkCallControl); //eslint-disable-line
    console.log('setYealinkCallControl - has handleStartCall:', typeof activeYealinkCallControl?.handleStartCall); //eslint-disable-line

    // Register this call control with the manager for message routing
    if (yealinkManager) {
      yealinkManager.setActiveCallControl(callControl);
    }

    const appName = selectAppName(store.getState());
    const token = nsToken.getDecoded();
    const firstLoginSettings = localStorage.getItem(`${appName}-${token.user}_onboarding`);
    const isOnboardingSettings = firstLoginSettings === ONBOARD_SETUP || firstLoginSettings === null;

    if (showSnackbar) {
      logger.info(`${MODULE_NAME} - setYealinkCallControl - headset paired`);
      store.dispatch(setSnackbar({
        type: INFO,
        open: true,
        message: i18n.t('HEADSET_PAIRED').replace('%s', getName(callControl)),
        ...(!window.location.pathname.includes('/settings') && {
          action: isOnboardingSettings ? null : {
            label: i18n.t('Settings'),
            link: '/settings',
          },
        }),
        duration: 3000,
      }));
    }

    const userId = selectUserId(store.getState());
    localStorage.setItem(`${appName}-${userId}_yealinkDevice`, getName(callControl));

    store.dispatch(setYealinkDevice(callControl));
    console.log("setting yealink device to Yealink");  //eslint-disable-line
    store.dispatch(setHeadsetType('yealink'));
    localStorage.setItem(`${appName}-${userId}_headsetType`, 'yealink');
    hidState.initOngoing = true;

    // Only subscribe if callControl has the expected structure
    if (callControl && callControl.muteState && callControl.holdState && callControl.ongoingCalls) {
      yealinkSubscribe(callControl); // Add subscription to events
    } else {
      logger.warn(`${MODULE_NAME} - setYealinkCallControl - callControl missing observables, skipping subscription`);
    }
    const count = selectActiveCount(store.getState());
    logger.info(`${MODULE_NAME} - setYealinkCallControl - start calls`, count);
    // Start calls on the Yealink headset if there are active calls
    if (count > 0) {
      const callSessions = selectCallSessions(store.getState());

      // Add all active call sessions to answeredCallSessions for tracking
      callSessions.forEach((session) => {
        if (session.status === 'accepted' && !hidState.answeredCallSessions.includes(session.id)) {
          addAnsweredCall(session.id);
          logger.info(`${MODULE_NAME} - setYealinkCallControl - added call ${session.id} to answeredCallSessions`);
        }
      });

      _.times(count, () => yealink.offHook());
      // Reset initOngoing after starting calls
      hidState.initOngoing = false;
      console.log('setYealinkCallControl - reset hidState.initOngoing to false after starting calls'); //eslint-disable-line
    }
  } else {
    logger.info(`${MODULE_NAME} - setYealinkCallControl - no matching yealink device`);
    store.dispatch(setYealinkDevice(null));
  }
};

/**
 * Unified function to set any headset call control
 */
export const setHeadsetCallControl = (callControl, type, showSnackbar = true) => {
  debugger; //eslint-disable-line
  console.log("setHeadsetCallControl", type);  //eslint-disable-line
  if (type === 'jabra') {
    setCallControl(callControl, showSnackbar);
  } else if (type === 'yealink') {
    setYealinkCallControl(callControl, showSnackbar);
  }
};

/**
 * Handles setting and switching between hid devices
 * Removes any previous listeners
 * Adds subscription listeners to act on the active call session
 * @param deviceId
 * @param showSnackbar
 */
export const setCallControlByDeviceId = async (deviceId, showSnackbar = true) => {
  debugger; //eslint-disable-line
  logger.info(`${MODULE_NAME} - setCallControlByDeviceId - device id`, deviceId);

  const deviceLabel = await getDeviceLabel(deviceId);
  logger.info(`${MODULE_NAME} - setCallControlByDeviceId - deviceLabel`, deviceLabel);

  if (deviceLabel) {
    const callControl = getHidDeviceByLabel(deviceLabel);
    logger.info(`${MODULE_NAME} - setCallControlByDeviceId - callControl`, callControl);

    const prevCallControl = selectHidDevice(store.getState());

    // do nothing if the call control is already set
    if (prevCallControl
      && callControl
      && getName(callControl) === getName(prevCallControl)
    ) {
      return;
    }

    if (prevCallControl) {
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - unsubscribe to previous hid call control`, callControl);
      hidUnsubscribe(prevCallControl);
      const count = selectActiveCount(store.getState());
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - remove previous calls`, count);
      _.times(count, prevCallControl.endCall);
    }

    if (callControl) {
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - switch to new call control`, callControl);

      // Store Jabra device at module level to avoid Redux serialization
      activeJabraCallControl = callControl;
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - stored Jabra device at module level`);

      if (showSnackbar) {
        logger.info(`${MODULE_NAME} - setCallControlByDeviceId - headset paired`);
        store.dispatch(setSnackbar({
          type: INFO,
          open: true,
          message: i18n.t('HEADSET_PAIRED').replace('%s', getName(callControl)),
          duration: 3000,
        }));
      }

      store.dispatch(setHidDevice(callControl));
      hidState.initOngoing = true;
      hidSubscribe(callControl);
      const count = selectActiveCount(store.getState());
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - start calls`, count);
      if (activeJabraCallControl && typeof activeJabraCallControl.startCall === 'function') {
        _.times(count, () => activeJabraCallControl.startCall());
      }
    } else {
      logger.info(`${MODULE_NAME} - setCallControlByDeviceId - no matching hid device`);
      activeJabraCallControl = null;
      store.dispatch(setHidDevice(null));
    }
  }
};

/**
 * Adds a flag that indicates if the device is paired or not
 * @returns {Promise<void>}
 */
const updateDeviceLabels = async () => {
  const inputDevices = selectInputDevices(store.getState());
  const outputDevices = selectOutputDevices(store.getState());

  const updatedInput = inputDevices.map((device) => {
    const headsetDevice = getHeadsetDeviceByLabel(device?.label);
    return {
      deviceId: device.deviceId,
      groupId: device.groupId,
      kind: device.kind,
      label: device.label,
      paired: !!headsetDevice,
      webhid: device.label?.toLowerCase().includes('jabra') 
        || device.label?.toLowerCase().includes('yealink'),
      headsetType: headsetDevice?.type || null,
    };
  });

  const updatedOutput = outputDevices.map((device) => {
    const headsetDevice = getHeadsetDeviceByLabel(device?.label);
    return {
      deviceId: device.deviceId,
      groupId: device.groupId,
      kind: device.kind,
      label: device.label,
      paired: !!headsetDevice,
      webhid: device.label?.toLowerCase().includes('jabra') 
        || device.label?.toLowerCase().includes('yealink'),
      headsetType: headsetDevice?.type || null,
    };
  });

  store.dispatch(setInputDevices(updatedInput));
  store.dispatch(setOutputDevices(updatedOutput));
};

/**
 * Process pre-selected headset devices (called after WebHID selection)
 * @param {string} type - 'jabra' or 'yealink'
 * @param {Array} devices - Pre-selected devices from WebHID
 * @returns {Promise<{success: boolean, devices: Array, type: string}>}
 */
export const processSelectedHeadsetDevices = async (type, devices) => {
  logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - type: ${type}`, devices);
  updateHidState({ pairing: true });

  try {
    if (type === 'yealink' && devices.length > 0) {
      // Initialize Yealink manager if needed
      if (!yealinkManager) {
        yealinkManager = new YealinkManager();
        yealinkCCFactory = new YealinkCallControlFactory(yealinkManager);
        await yealinkManager.init();
      }

      // Add devices to SDK directly using the original device objects
      devices.forEach((device) => {
        logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Adding device to SDK:`, device.productName);

        // Add device directly to the SDK if it has the original WebHID device
        if (device.originalDevice) {
          // Import the yealink SDK's addDevice function logic directly
          try {
            const hidDevice = device.originalDevice;
            let deviceAdded = false;

            // Check if device has proper collections structure for SDK
            if (hidDevice.collections) {
              // eslint-disable-next-line no-restricted-syntax
              for (const collection of hidDevice.collections) {
                if ((collection.usage === 0x0005 && collection.usagePage === 0x000B)) {
                  logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Device has proper HID collection:`, device.productName);
                  deviceAdded = true;
                  break;
                }
              }
            }

            // If device structure is compatible, add it directly to the SDK
            if (deviceAdded) {
              logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Adding device directly to SDK:`, device.productName);
              // eslint-disable-next-line no-console
              console.log('[processSelectedHeadsetDevices] Calling addYealinkDevice with:', hidDevice.productName);
              // eslint-disable-next-line no-console
              console.log('[processSelectedHeadsetDevices] vendorId:', hidDevice.vendorId);

              // Call addDevice directly instead of trying to trigger onconnect event
              addYealinkDevice(hidDevice);

              console.log('[processSelectedHeadsetDevices] After addYealinkDevice call'); // eslint-disable-line no-console
            }
          } catch (error) {
            logger.error(`${MODULE_NAME} - processSelectedHeadsetDevices - Direct SDK add failed:`, error);
          }
        }
      });

      // Add delay to ensure SDK processing completes before creating call control
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });

      // Update Redux state with Yealink devices (with deduplication)
      const currentYealinkDevices = selectYealinkDevices(store.getState());
      const newDevices = devices.filter((newDevice) => !currentYealinkDevices.some((existing) => {
        const existingName = getName(existing);
        const newName = newDevice.productName || newDevice.name;
        return existingName && newName && existingName === newName;
      }));
      
      if (newDevices.length > 0) {
        store.dispatch(setYealinkDevices([...currentYealinkDevices, ...newDevices]));
        logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Added ${newDevices.length} new Yealink devices`);
      } else {
        logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - All Yealink devices were duplicates, skipped`);
      }
      
      store.dispatch(setYealinkDevice(devices[0]));
      store.dispatch(setHeadsetType('yealink'));

      // Check localStorage for saved device preference
      const appName = selectAppName(store.getState());
      const userId = selectUserId(store.getState());
      const savedDeviceName = localStorage.getItem(`${appName}-${userId}_yealinkDevice`);

      // Find the saved device in the array, or fall back to first device
      let deviceToActivate = devices[0];
      if (savedDeviceName) {
        const savedDevice = devices.find((d) => d.productName === savedDeviceName);
        if (savedDevice) {
          logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Found saved device preference:`, savedDeviceName);
          deviceToActivate = savedDevice;
        }
      }

      // Set the selected device as active
      if (deviceToActivate) {
        logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Creating call control for:`, deviceToActivate.productName);
        const callControl = await yealinkCCFactory.createMultiCallControl(deviceToActivate);
        setYealinkCallControl(callControl, true);
        logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Call control created successfully`);
      }
    } else if (type === 'jabra' && devices.length > 0) {
      // For Jabra devices, DON'T manually process them here
      // The Jabra SDK's deviceAdded event handler (in initHid) will automatically
      // detect and process these devices when they're authorized via WebHID
      logger.info(
        `${MODULE_NAME} - processSelectedHeadsetDevices - `
        + 'Jabra device(s) authorized via WebHID, waiting for SDK deviceAdded event',
      );

      store.dispatch(setHeadsetType('jabra'));

      // The deviceAdded handler will:
      // 1. Create the proper call control object
      // 2. Add it to Redux
      // 3. Set it as the active device
      // We just need to wait a moment for this to happen
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });

      // Verify that the device was added by the SDK
      const currentHidDevices = selectHidDevices(store.getState());
      logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - After SDK processing, hidDevices count: ${currentHidDevices.length}`);

      // OLD CODE REMOVED - Was causing duplicate devices and "does not support call control" errors
      // The Jabra SDK handles everything automatically via its deviceAdded event handler

      // DON'T set pairing = false here for Jabra - let the deviceAdded handler do it
      // Otherwise the handler won't call setCallControl because pairing is already false
      // The deviceAdded handler will set pairing = false after setting up the device
    }

    // DON'T set pairing = false here for either Jabra or Yealink
    // Both device types have deviceAdded handlers that will clear the flag after setting audio devices
    // If we clear it here, the deviceAdded handler won't set the audio devices because pairing is already false

    // Post-pairing cleanup and state updates
    if (devices.length > 0) {
      logger.info(`${MODULE_NAME} - processSelectedHeadsetDevices - Post-pairing cleanup for ${type}`);
      
      // Update device labels to reflect paired status
      await updateDeviceLabels();
      
      // Clear any stale localStorage values that might affect future pairing logic
      const appName = selectAppName(store.getState());
      const token = nsToken.getDecoded();
      const userId = token.user;
      
      // Clear any temporary pairing preference markers
      localStorage.removeItem(`${appName}-${userId}_pairing_attempt_timestamp`);
      localStorage.removeItem(`${appName}-${userId}_last_pairing_strategy`);
      
      // Update stored headset type preference to reflect the successfully paired type
      localStorage.setItem(`${appName}-${userId}_preferred_headset_type`, type);
      
      // Log the successful pairing for debugging
      logger.info(
        `${MODULE_NAME} - processSelectedHeadsetDevices - Successfully paired ${type} device(s). `
        + 'Updated device labels and cleared stale localStorage values.',
      );
    }

    return {
      success: devices.length > 0,
      devices,
      type,
    };
  } catch (error) {
    logger.error(`${MODULE_NAME} - processSelectedHeadsetDevices - error:`, error);
    updateHidState({ pairing: false });

    return {
      success: false,
      devices: [],
      type,
      error: error.message,
    };
  }
};

// Debounce mechanism to prevent duplicate consent dialogs
let lastConsentCheck = 0;
const CONSENT_DEBOUNCE_MS = 30000; // 30 seconds

/**
 * Tests for one or more HID devices to determines
 * if the hid consent dialog should be shown and shows it
 * @returns {Promise<boolean>}
 */
export const devicesNeedConsent = async (isDeviceChange = false) => {
  logger.info(`${MODULE_NAME} - devicesNeedConsent - isDeviceChange:`, isDeviceChange);

  // Debounce mechanism to prevent duplicate consent dialogs
  const now = Date.now();
  if (isDeviceChange && (now - lastConsentCheck) < CONSENT_DEBOUNCE_MS) {
    logger.info(`${MODULE_NAME} - devicesNeedConsent - Skipping due to debounce (${now - lastConsentCheck}ms ago)`);
    return;
  }

  if (isDeviceChange) {
    lastConsentCheck = now;
  }

  const appName = selectAppName(store.getState());
  const token = nsToken.getDecoded();
  const firstLogin = localStorage.getItem(`${appName}-${token.user}_onboarding`);

  logger.info(`${MODULE_NAME} - devicesNeedConsent - first login`, firstLogin);

  // Check during onboarding setup, after completion, or on first login (null)
  if (firstLogin === ONBOARD_COMPLETE || firstLogin === ONBOARD_SETUP || firstLogin === null) {
    const devices = await getMediaDevices();
    logger.info(`${MODULE_NAME} - devicesNeedConsent - devices`, devices);

    // Filter to only check compatible headset devices, not all media devices
    const compatibleDevices = devices.filter((device) => {
      const deviceLabel = device?.label?.toLowerCase() || '';
      return deviceLabel.includes('jabra') || deviceLabel.includes('yealink');
    });

    logger.info(
      `${MODULE_NAME} - devicesNeedConsent - found ${compatibleDevices.length} compatible devices:`,
      compatibleDevices.map((d) => d.label),
    );

    let res = await Promise.all(compatibleDevices.map(async (device) => {
      const deviceLabel = device?.label;
      let callControl;
      if (deviceLabel?.toLowerCase().includes('jabra')) {
        callControl = getHidDeviceByLabel(deviceLabel);
        logger.info(`${MODULE_NAME} - devicesNeedConsent - checking Jabra device: ${deviceLabel}, found:`, !!callControl);
      }
      if (deviceLabel?.toLowerCase().includes('yealink')) {
        callControl = getYealinkDeviceByLabel(deviceLabel);
        logger.info(`${MODULE_NAME} - devicesNeedConsent - checking Yealink device: ${deviceLabel}, found:`, !!callControl);
      }
      return !!callControl;
    }));

    logger.info(`${MODULE_NAME} - devicesNeedConsent - paired status array`, res);

    // remove nulls (non hid devices)
    res = res.filter((e) => e != null);

    logger.info(`${MODULE_NAME} - devicesNeedConsent - every device has consent`, _.every(res));

    const dontAsk = localStorage.getItem(`${appName}-${token.user}_hid_dont_ask_again`);
    const hidSetupDisplayed = Cookies.get(`${appName}-${token.user}_onboarding_hid_setup_displayed`);
    const hidDisplayedDebounce = Cookies.get(`${appName}-${token.user}_hid_displayed`);

    logger.info(
      `${MODULE_NAME} - devicesNeedConsent - decision factors - `
      + `dontAsk: ${dontAsk}, hidSetupDisplayed: ${hidSetupDisplayed}, `
      + `hidDisplayedDebounce: ${hidDisplayedDebounce}, compatibleDevices: ${compatibleDevices.length}, `
      + `res.length: ${res.length}, _.every(res): ${_.every(res)}`,
    );

    if (hidDisplayedDebounce !== 'true') {
      // Show consent if:
      // 1. Device change occurred AND don't ask is false AND hid setup not displayed AND compatible devices need pairing
      // 2. OR it's initial onboarding (not device change) AND compatible devices exist AND not all are paired
      const shouldShowConsent = (
        (isDeviceChange && dontAsk !== 'true' && hidSetupDisplayed !== 'true' && res.length && !_.every(res))
        || (!isDeviceChange && compatibleDevices.length > 0 && res.length && !_.every(res) && dontAsk !== 'true')
      );

      logger.info(`${MODULE_NAME} - devicesNeedConsent - shouldShowConsent: ${shouldShowConsent}`);

      if (shouldShowConsent) {
        logger.info(
          `${MODULE_NAME} - devicesNeedConsent - show hid consent - isDeviceChange: ${isDeviceChange}, `
          + `compatible devices: ${compatibleDevices.length}, paired devices: ${res.filter(Boolean).length}`,
        );

        Cookies.set(`${appName}-${token.user}_hid_displayed`, true, {
          expires: new Date(new Date().getTime() + 15 * 1000),
        });

        store.dispatch(setShowHidConsent(true));
      } else {
        logger.info(
          `${MODULE_NAME} - devicesNeedConsent - hide hid consent - isDeviceChange: ${isDeviceChange}, `
          + `compatible devices: ${compatibleDevices.length}, paired devices: ${res.filter(Boolean).length}`,
        );
        store.dispatch(setShowHidConsent(false));
      }
    }
  }
};

/**
 * Get HID devices and listen for changes
 * @returns {Promise<void>}
 */
const initHid = async () => {
  logger.info(`${MODULE_NAME} - initHid`);

  if (navigator.hid) {
    logger.info(`${MODULE_NAME} - initHid - WebHID supported`);
  } else {
    logger.warn(`${MODULE_NAME} - initHid - WebHID NOT supported`);
    return;
  }

  try {
    // Initialize Jabra
    const jabra = await hidInit({
      appId: 'webphone',
      appName: 'webphone',
      transport: RequestedBrowserTransport.CHROME_EXTENSION_WITH_WEB_HID_FALLBACK,
      partnerKey: '22c2-4b359266-8d11-4425-9bce-6f61eadca050',
    });
    ccFactory = new EasyCallControlFactory(jabra);

    // Initialize Yealink
    yealinkManager = new YealinkManager();
    yealinkCCFactory = new YealinkCallControlFactory(yealinkManager);
    await yealinkManager.init();
    debugger; //eslint-disable-line
    jabra.deviceAdded.subscribe(async (addedDevice) => {
      debugger; //eslint-disable-line
      logger.info(`${MODULE_NAME} - initHid - deviceAdded`, addedDevice);
      
      // Only process Jabra devices (prevent Yealink devices from being added here)
      const deviceName = addedDevice?.name || addedDevice?.productName || '';
      if (deviceName.toLowerCase().includes('yealink')) {
        logger.info(`${MODULE_NAME} - initHid - deviceAdded skipping Yealink device in Jabra handler:`, deviceName);
        return;
      }

      if (ccFactory.supportsEasyCallControl(addedDevice)) {
        // add device list to list in redux
        const callControl = await ccFactory.createMultiCallControl(addedDevice);

        // Store call control at module level to preserve methods
        const existingIndex = jabraCallControls.findIndex((cc) => getName(cc) === getName(callControl));
        if (existingIndex >= 0) {
          jabraCallControls[existingIndex] = callControl;
          logger.info(`${MODULE_NAME} - initHid - updated existing Jabra call control in module array`);
        } else {
          jabraCallControls.push(callControl);
          logger.info(`${MODULE_NAME} - initHid - added new Jabra call control to module array, total: ${jabraCallControls.length}`);
        }

        const hidDevices = selectHidDevices(store.getState());
        
        // Check for duplicates before adding - compare by multiple identifiers
        logger.info(
          `${MODULE_NAME} - initHid - checking for duplicates, current hidDevices:`,
          hidDevices.map((d) => ({
            name: getName(d),
            vendorId: d?.device?.vendorId || d?.vendorId,
            productId: d?.device?.productId || d?.productId,
          })),
        );
        const deviceExists = hidDevices.some((existingDevice) => {
          // Method 1: Compare device IDs (for proper Jabra call controls)
          const existingId = existingDevice?.device?.id;
          const newId = addedDevice?.id;
          if (existingId && newId && existingId.equals && existingId.equals(newId)) {
            logger.info(`${MODULE_NAME} - initHid - duplicate found by device ID`);
            return true;
          }

          // Method 2: Compare by vendor/product ID (for fallback devices)
          const existingVendorId = existingDevice?.device?.vendorId || existingDevice?.vendorId;
          const existingProductId = existingDevice?.device?.productId || existingDevice?.productId;
          const newVendorId = addedDevice?.vendorId;
          const newProductId = addedDevice?.productId;
          if (existingVendorId && existingProductId && newVendorId && newProductId) {
            if (existingVendorId === newVendorId && existingProductId === newProductId) {
              logger.info(
                `${MODULE_NAME} - initHid - duplicate found by vendor/product ID: `
                + `${existingVendorId}:${existingProductId}`,
              );
              return true;
            }
          }

          // Method 3: Compare by device name
          const existingName = existingDevice?.name || existingDevice?.productName
            || existingDevice?.device?.name || existingDevice?.device?.productName;
          const newName = addedDevice?.name || addedDevice?.productName;
          if (existingName && newName && existingName === newName) {
            logger.info(`${MODULE_NAME} - initHid - duplicate found by name: "${existingName}" === "${newName}"`);
            return true;
          }

          return false;
        });
        
        if (!deviceExists) {
          store.dispatch(setHidDevices([...hidDevices, callControl]));
          logger.info(`${MODULE_NAME} - initHid - deviceAdded new device`, [...hidDevices, callControl]);
        } else {
          logger.info(`${MODULE_NAME} - initHid - deviceAdded duplicate device ignored`, addedDevice);
        }

        // Logging moved to conditional blocks above

        if (hidState.pairing) {
          updateHidState({ pairing: false });

          const userId = selectUserId(store.getState());
          const appName = selectAppName(store.getState());
          const iDevices = selectInputDevices(store.getState());
          const oDevices = selectOutputDevices(store.getState());

          const newInput = iDevices.find((device) => device.kind === 'audioinput'
            && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));
          const newOutput = oDevices.find((device) => device.kind === 'audiooutput'
            && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));

          store.dispatch(setInputDevice(_.get(newInput, 'deviceId')));
          store.dispatch(setOutputDevice(_.get(newOutput, 'deviceId')));
          store.dispatch(setOutputDeviceRinger(_.get(newOutput, 'deviceId')));
          localStorage.setItem(`${appName}-${userId}_inputAudioDevice`, _.get(newInput, 'deviceId'));
          localStorage.setItem(`${appName}-${userId}_outputAudioDevice`, _.get(newOutput, 'deviceId'));
          localStorage.setItem(`${appName}-${userId}_outputAudioDeviceRinger`, _.get(newOutput, 'deviceId'));
          updateElementSinkId();

          setCallControl(callControl);
        }

        // Only check consent for devices added after initial load
        // During initial load, the consent check is handled by the delayed timeout in initDevices
        if (!hidState.isInitialLoad) {
          logger.info(`${MODULE_NAME} - initHid - deviceAdded checking consent (not initial load)`);
          await devicesNeedConsent(true);
        } else {
          logger.info(`${MODULE_NAME} - initHid - deviceAdded skipping consent check (initial load)`);
        }
      }
    });

    jabra.deviceRemoved.subscribe((removedDevice) => {
      logger.info(`${MODULE_NAME} - deviceRemoved`, removedDevice);

      let hidDevices = selectHidDevices(store.getState());
      hidDevices = [...hidDevices];
      const index = hidDevices.findIndex(
        ({ device }) => device && device.id && removedDevice && removedDevice.id && device.id.equals(removedDevice.id),
      );

      if (index > -1) {
        hidUnsubscribe(hidDevices[index]);

        hidDevices.splice(index, 1);
        store.dispatch(setHidDevices(hidDevices));

        // If this was the active device, clear it
        const activeDevice = selectHidDevice(store.getState());
        if (activeDevice && activeDevice.device && activeDevice.device.id
          && removedDevice && removedDevice.id && activeDevice.device.id.equals(removedDevice.id)) {
          const appName = selectAppName(store.getState());
          const user = selectUserId(store.getState());

          store.dispatch(setHidDevice(null));
          store.dispatch(setHeadsetType('none'));
          localStorage.removeItem(`${appName}-${user}_hidDevice`);
          localStorage.removeItem(`${appName}-${user}_headsetType`);
        }
      }
    });

    // Yealink device listeners
    yealinkManager.deviceAdded.subscribe(async (addedDevice) => {
      debugger; //eslint-disable-line
      console.log("Yealink device added:", addedDevice);  //eslint-disable-line
      logger.info(`${MODULE_NAME} - initYealink - deviceAdded`, addedDevice);
      
      // Only process Yealink devices (prevent Jabra devices from being added here)
      const deviceName = addedDevice?.name || addedDevice?.productName || '';
      if (deviceName.toLowerCase().includes('jabra')) {
        logger.info(`${MODULE_NAME} - initYealink - deviceAdded skipping Jabra device in Yealink handler:`, deviceName);
        return;
      }

      if (yealinkCCFactory.supportsEasyCallControl(addedDevice)) {
        // Add device to Yealink devices list in redux
        const callControl = await yealinkCCFactory.createMultiCallControl(addedDevice);
        const yealinkDevices = selectYealinkDevices(store.getState());
        
        // Check for duplicates before adding - compare by multiple identifiers
        const deviceExists = yealinkDevices.some((existingDevice) => {
          // Method 1: Compare device IDs (for proper Yealink call controls)
          const existingId = existingDevice?.device?.id;
          const newId = addedDevice?.id;
          if (existingId && newId && existingId.equals && existingId.equals(newId)) {
            return true;
          }
          
          // Method 2: Compare by vendor/product ID (for fallback devices)
          const existingVendorId = existingDevice?.device?.vendorId || existingDevice?.vendorId;
          const existingProductId = existingDevice?.device?.productId || existingDevice?.productId;
          const newVendorId = addedDevice?.vendorId;
          const newProductId = addedDevice?.productId;
          if (existingVendorId && existingProductId && newVendorId && newProductId) {
            if (existingVendorId === newVendorId && existingProductId === newProductId) {
              return true;
            }
          }
          
          // Method 3: Compare by device name
          const existingName = existingDevice?.name || existingDevice?.productName 
            || existingDevice?.device?.name || existingDevice?.device?.productName;
          const newName = addedDevice?.name || addedDevice?.productName;
          if (existingName && newName && existingName === newName) {
            return true;
          }
          
          return false;
        });
        
        if (!deviceExists) {
          store.dispatch(setYealinkDevices([...yealinkDevices, callControl]));
          logger.info(`${MODULE_NAME} - initYealink - deviceAdded new device`, [...yealinkDevices, callControl]);
        } else {
          logger.info(`${MODULE_NAME} - initYealink - deviceAdded duplicate device ignored`, addedDevice);
        }

        // Don't add Yealink devices to Jabra hidDevices list - they have their own
        if (hidState.pairing) {
          updateHidState({ pairing: false });
          const userId = selectUserId(store.getState());
          const appName = selectAppName(store.getState());
          const iDevices = selectInputDevices(store.getState());
          const oDevices = selectOutputDevices(store.getState());

          const newInput = iDevices.find((device) => device.kind === 'audioinput'
            && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));
          const newOutput = oDevices.find((device) => device.kind === 'audiooutput'
            && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));

          store.dispatch(setInputDevice(_.get(newInput, 'deviceId')));
          store.dispatch(setOutputDevice(_.get(newOutput, 'deviceId')));
          store.dispatch(setOutputDeviceRinger(_.get(newOutput, 'deviceId')));
          localStorage.setItem(`${appName}-${userId}_inputAudioDevice`, _.get(newInput, 'deviceId'));
          localStorage.setItem(`${appName}-${userId}_outputAudioDevice`, _.get(newOutput, 'deviceId'));
          localStorage.setItem(`${appName}-${userId}_outputAudioDeviceRinger`, _.get(newOutput, 'deviceId'));
          updateElementSinkId();

          setYealinkCallControl(callControl);
        }
      }
    });

    yealinkManager.deviceRemoved.subscribe((removedDevice) => {
      debugger; //eslint-disable-line
      logger.info(`${MODULE_NAME} - yealink deviceRemoved`, removedDevice);

      let yealinkDevices = selectYealinkDevices(store.getState());
      yealinkDevices = [...yealinkDevices];
      const index = yealinkDevices.findIndex(
        ({ device }) => device && device.id && removedDevice && removedDevice.id && device.id.equals(removedDevice.id),
      );

      if (index > -1) {
        yealinkDevices.splice(index, 1);
        store.dispatch(setYealinkDevices(yealinkDevices));

        // If this was the active device, clear it
        const activeDevice = selectYealinkDevice(store.getState());
        if (activeDevice && activeDevice.device && activeDevice.device.id
          && removedDevice && removedDevice.id && activeDevice.device.id.equals(removedDevice.id)) {
          const appName = selectAppName(store.getState());
          const user = selectUserId(store.getState());

          store.dispatch(setYealinkDevice(null));
          store.dispatch(setHeadsetType('none'));
          localStorage.removeItem(`${appName}-${user}_yealinkDevice`);
          localStorage.removeItem(`${appName}-${user}_headsetType`);
        }
      }
    });
  } catch (e) {
    debugger; //eslint-disable-line
    logger.error(`${MODULE_NAME} - initHid error`, e);
    bugsnagClient.notify(e);
    console.error(e);
  }
};

// Export the initHid function
export { initHid };

/**
 * Handle headset pairing for both Jabra and Yealink devices
 * @param {string} type - 'jabra', 'yealink', or 'auto' to try both
 * @returns {Promise<{success: boolean, devices: Array, type: string}>}
 */
export const handleHeadsetPair = async (type = 'auto') => {
  logger.info(`${MODULE_NAME} - handleHeadsetPair - type: ${type}`);
  updateHidState({ pairing: true });

  let pairedDevices = [];
  let pairedType = null;

  try {
    // Get optimal strategy to determine if fallback should be offered
    // eslint-disable-next-line no-use-before-define
    const strategyResult = await detectOptimalPairingStrategy();
    const { shouldOfferFallback, fallback } = strategyResult;

    logger.info(
      `${MODULE_NAME} - handleHeadsetPair - Strategy: primary=${type}, `
      + `shouldOfferFallback=${shouldOfferFallback}, fallback=${fallback}`,
    );

    // Initialize managers first (without user gesture)
    if (!yealinkManager && (type === 'yealink' || type === 'auto')) {
      yealinkManager = new YealinkManager();
      yealinkCCFactory = new YealinkCallControlFactory(yealinkManager);
      await yealinkManager.init();
    }

    // For Yealink, call requestDevice immediately to preserve user gesture
    if (type === 'yealink' || type === 'auto') {
      try {
        const yealinkDevices = await yealinkManager.requestDevice();
        if (yealinkDevices.length > 0) {
          pairedDevices = yealinkDevices;
          pairedType = 'yealink';
          logger.info(`${MODULE_NAME} - handleHeadsetPair - Yealink devices paired:`, yealinkDevices);
        }
      } catch (error) {
        logger.warn(`${MODULE_NAME} - handleHeadsetPair - Yealink pairing failed:`, error);
      }

      // If Yealink pairing didn't succeed, try fallback if available
      if (pairedDevices.length === 0) {
        logger.info(`${MODULE_NAME} - handleHeadsetPair - Yealink pairing unsuccessful`);

        // Only try fallback to Jabra if strategy says we should
        if (shouldOfferFallback && fallback === 'jabra') {
          logger.info(`${MODULE_NAME} - handleHeadsetPair - Offering Jabra as fallback`);
          try {
            // Ensure Jabra SDK is initialized before pairing
            if (!ccFactory) {
              logger.info(`${MODULE_NAME} - handleHeadsetPair - Initializing Jabra SDK for fallback`);
              await initHid();
            }
            const { webHidPairing } = await import('@gnaudio/jabra-js');
            const jabraDevices = await webHidPairing();
            if (jabraDevices.length > 0) {
              pairedDevices = jabraDevices;
              pairedType = 'jabra';
              logger.info(`${MODULE_NAME} - handleHeadsetPair - Jabra devices paired:`, jabraDevices);
            }
          } catch (jabraError) {
            logger.warn(`${MODULE_NAME} - handleHeadsetPair - Jabra pairing also failed:`, jabraError);
          }
        } else {
          logger.info(`${MODULE_NAME} - handleHeadsetPair - No fallback offered (no unpaired Jabra devices)`);
        }
      }
    } else if (type === 'jabra') {
      // Jabra first - ensure SDK is initialized
      if (!ccFactory) {
        logger.info(`${MODULE_NAME} - handleHeadsetPair - Initializing Jabra SDK first`);
        await initHid();
      }
      try {
        const { webHidPairing } = await import('@gnaudio/jabra-js');
        const jabraDevices = await webHidPairing();
        if (jabraDevices.length > 0) {
          pairedDevices = jabraDevices;
          pairedType = 'jabra';
          logger.info(`${MODULE_NAME} - handleHeadsetPair - Jabra devices paired:`, jabraDevices);
        }
      } catch (jabraError) {
        logger.warn(`${MODULE_NAME} - handleHeadsetPair - Jabra pairing failed:`, jabraError);
      }

      // If Jabra pairing didn't succeed, try fallback if available
      if (pairedDevices.length === 0) {
        logger.info(`${MODULE_NAME} - handleHeadsetPair - Jabra pairing unsuccessful`);

        // Only try fallback to Yealink if strategy says we should
        if (shouldOfferFallback && fallback === 'yealink') {
          logger.info(`${MODULE_NAME} - handleHeadsetPair - Offering Yealink as fallback`);
          try {
            if (!yealinkManager) {
              yealinkManager = new YealinkManager();
              yealinkCCFactory = new YealinkCallControlFactory(yealinkManager);
              await yealinkManager.init();
            }
            const yealinkDevices = await yealinkManager.requestDevice();
            if (yealinkDevices.length > 0) {
              pairedDevices = yealinkDevices;
              pairedType = 'yealink';
              logger.info(`${MODULE_NAME} - handleHeadsetPair - Yealink devices paired:`, yealinkDevices);
            }
          } catch (yealinkError) {
            logger.warn(`${MODULE_NAME} - handleHeadsetPair - Yealink pairing also failed:`, yealinkError);
          }
        } else {
          logger.info(`${MODULE_NAME} - handleHeadsetPair - No fallback offered (no unpaired Yealink devices)`);
        }
      }
    }

    if (pairedDevices.length > 0) {
      // For both Jabra and Yealink, use processSelectedHeadsetDevices
      // For Jabra: This lets the SDK's deviceAdded event handler process devices automatically
      // For Yealink: This handles the manual device setup
      await processSelectedHeadsetDevices(pairedType, pairedDevices);
    }

    // DON'T clear pairing flag here for either Jabra or Yealink
    // Both device types have deviceAdded handlers that will clear the flag after setting audio devices
    // If we clear it here, the deviceAdded handler won't set the audio devices because pairing is already false

    return {
      success: pairedDevices.length > 0,
      devices: pairedDevices,
      type: pairedType,
    };
  } catch (error) {
    logger.error(`${MODULE_NAME} - handleHeadsetPair - error:`, error);
    updateHidState({ pairing: false });

    return {
      success: false,
      devices: [],
      type: null,
      error: error.message,
    };
  }
};

/**
 * Handle Yealink headset pairing specifically
 * @returns {Promise<{success: boolean, devices: Array, type: string}>}
 */
export const handleYealinkPair = async () => {
  logger.info(`${MODULE_NAME} - handleYealinkPair`);
  updateHidState({ pairing: true });

  try {
    // Initialize manager first (without user gesture)
    if (!yealinkManager) {
      yealinkManager = new YealinkManager();
      yealinkCCFactory = new YealinkCallControlFactory(yealinkManager);
      await yealinkManager.init();
    }

    // Request Yealink device pairing (preserves user gesture)
    const yealinkDevices = await yealinkManager.requestDevice();
    updateHidState({ pairing: false });

    if (yealinkDevices.length > 0) {
      logger.info(`${MODULE_NAME} - handleYealinkPair - Success:`, yealinkDevices);
      return {
        success: true,
        devices: yealinkDevices,
        type: 'yealink',
      };
    }

    logger.info(`${MODULE_NAME} - handleYealinkPair - No devices paired`);
    return {
      success: false,
      devices: [],
      type: 'yealink',
    };
  } catch (error) {
    logger.error(`${MODULE_NAME} - handleYealinkPair - error:`, error);
    updateHidState({ pairing: false });

    return {
      success: false,
      devices: [],
      type: 'yealink',
      error: error.message,
    };
  }
};

/**
 * Tests devices to determine if the hid consent dialog should be shown and shows it
 * Only shows consent if there are unpaired compatible devices.
 * @param deviceId - optional specific device to check, if not provided checks all devices
 * @returns {Promise<void>}
 */
export const deviceNeedsConsent = async (deviceId = null) => {
  logger.info(`${MODULE_NAME} - deviceNeedsConsent - device id`, deviceId);

  const appName = selectAppName(store.getState());
  const token = nsToken.getDecoded();
  const firstLogin = localStorage.getItem(`${appName}-${token.user}_onboarding`);

  logger.info(`${MODULE_NAME} - deviceNeedsConsent - first login`, firstLogin);

  // only ask for consent when the user has logged in before or finished the first time site tour
  if (firstLogin === ONBOARD_COMPLETE) {
    const dontAsk = localStorage.getItem(`${appName}-${token.user}_hid_dont_ask_again`);

    if (dontAsk === 'true') {
      logger.info(`${MODULE_NAME} - deviceNeedsConsent - user chose don't ask again`);
      return;
    }

    let devicesToCheck = [];

    if (deviceId) {
      // Check specific device if deviceId provided
      const devices = selectInputDevices(store.getState());
      const device = _.find(devices, { deviceId });
      if (device) {
        devicesToCheck = [device];
      }
    } else {
      // Check all available media devices
      try {
        const mediaDevices = await getMediaDevices();
        devicesToCheck = mediaDevices;
      } catch (error) {
        logger.error(`${MODULE_NAME} - deviceNeedsConsent - failed to get media devices`, error);
        return;
      }
    }

    // Find all compatible devices that are not already paired
    const unpairedCompatibleDevices = [];

    devicesToCheck.forEach((device) => {
      const deviceLabel = device?.label?.toLowerCase();
      if (deviceLabel?.includes('jabra') || deviceLabel?.includes('yealink')) {
        // Check if this device is already paired by looking for existing headset device
        const headsetDevice = getHeadsetDeviceByLabel(device.label);
        if (!headsetDevice) {
          unpairedCompatibleDevices.push({
            device,
            label: device.label,
            type: deviceLabel.includes('jabra') ? 'jabra' : 'yealink',
          });
        }
      }
    });

    logger.info(`${MODULE_NAME} - deviceNeedsConsent - unpaired compatible devices found`, unpairedCompatibleDevices.length);

    // Only show consent if there are unpaired compatible devices
    if (unpairedCompatibleDevices.length > 0) {
      logger.info(`${MODULE_NAME} - deviceNeedsConsent - showing consent for unpaired devices`, unpairedCompatibleDevices);
      store.dispatch(setShowHidConsent(true));
    } else {
      logger.info(`${MODULE_NAME} - deviceNeedsConsent - no unpaired compatible devices, not showing consent`);
    }
  }
};

/**
 * Detects the predominant headset type based on available media devices
 * @returns {Promise<string>} - 'jabra', 'yealink', or 'auto' if no clear preference
 */
export const detectPredominantHeadsetType = async () => {
  try {
    const devices = await getMediaDevices();
    let jabraCount = 0;
    let yealinkCount = 0;

    devices.forEach((device) => {
      const deviceLabel = device?.label?.toLowerCase();
      if (deviceLabel?.includes('jabra')) {
        jabraCount += 1;
      } else if (deviceLabel?.includes('yealink')) {
        yealinkCount += 1;
      }
    });

    logger.info(`${MODULE_NAME} - detectPredominantHeadsetType - Jabra: ${jabraCount}, Yealink: ${yealinkCount}`);

    if (jabraCount > yealinkCount) {
      return 'jabra';
    }
    if (yealinkCount > jabraCount) {
      return 'yealink';
    }
    return 'auto'; // fallback to auto if equal or no devices
  } catch (error) {
    logger.warn(`${MODULE_NAME} - detectPredominantHeadsetType - Error:`, error);
    return 'auto'; // fallback to auto on error
  }
};

/**
 * Determines optimal headset pairing strategy based on available devices and current pairing status
 * Prioritizes unpaired devices over already paired ones
 * @returns {Promise<{primary: string, shouldOfferFallback: boolean, fallback: string|null}>}
 *   - primary: 'jabra', 'yealink', or 'auto' - which vendor to try first
 *   - shouldOfferFallback: boolean - whether to offer the other vendor on cancel
 *   - fallback: 'jabra', 'yealink', or null - which vendor to fallback to
 */
export const detectOptimalPairingStrategy = async () => {
  try {
    const appName = selectAppName(store.getState());
    const token = nsToken.getDecoded();
    const userId = token.user;
    
    // Check for user's preferred headset type from successful pairings
    const preferredHeadsetType = localStorage.getItem(`${appName}-${userId}_preferred_headset_type`);
    logger.info(`${MODULE_NAME} - detectOptimalPairingStrategy - User's preferred headset type: ${preferredHeadsetType}`);
    
    const mediaDevices = await getMediaDevices();
    logger.info(`${MODULE_NAME} - detectOptimalPairingStrategy - Available media devices:`, mediaDevices);

    let hasYealink = false;
    let hasJabra = false;

    // Check what types of headsets are already connected
    mediaDevices.forEach((device) => {
      const deviceLabel = device?.label?.toLowerCase() || '';
      if (deviceLabel.includes('jabra')) {
        hasJabra = true;
      }
      if (deviceLabel.includes('yealink')) {
        hasYealink = true;
      }
    });

    logger.info(`${MODULE_NAME} - detectOptimalPairingStrategy - Detected headsets - Jabra: ${hasJabra}, Yealink: ${hasYealink}`);

    // Check which devices are already paired by looking at current state
    const currentJabraDevice = selectHidDevice(store.getState());
    const currentYealinkDevice = selectYealinkDevice(store.getState());
    const isJabraPaired = !!currentJabraDevice;
    const isYealinkPaired = !!currentYealinkDevice;

    logger.info(`${MODULE_NAME} - detectOptimalPairingStrategy - Device pairing status`
      + ` - Jabra paired: ${isJabraPaired}, Yealink paired: ${isYealinkPaired}`);

    // Determine optimal pairing order with improved logic
    let strategy = 'auto'; // Default fallback

    // Priority 1: Use user's preferred type if they have devices of that type available
    if (preferredHeadsetType && (
      (preferredHeadsetType === 'jabra' && hasJabra && !isJabraPaired)
      || (preferredHeadsetType === 'yealink' && hasYealink && !isYealinkPaired)
    )) {
      strategy = preferredHeadsetType;
      logger.info(`${MODULE_NAME} - detectOptimalPairingStrategy - Using preferred type: ${preferredHeadsetType}`);
    } else if (hasJabra && hasYealink) {
      // Priority 2: If both device types present - prioritize the unpaired one
      if (isYealinkPaired && !isJabraPaired) {
        // Yealink is already paired, try Jabra first
        strategy = 'jabra';
      } else if (isJabraPaired && !isYealinkPaired) {
        // Jabra is already paired, try Yealink first
        strategy = 'yealink';
      } else {
        // Both paired or both unpaired - use preferred type, or fallback to user's last successful type
        strategy = preferredHeadsetType || 'auto';
      }
    } else if (hasJabra && !hasYealink) {
      // Priority 3: Single device type available
      strategy = 'jabra';
    } else if (hasYealink && !hasJabra) {
      strategy = 'yealink';
    } else {
      // Priority 4: No devices detected - use preferred type or auto
      strategy = preferredHeadsetType || 'auto';
    }

    // Determine if we should offer fallback to the other vendor
    let shouldOfferFallback = false;
    let fallback = null;

    if (strategy === 'jabra' && hasYealink && !isYealinkPaired) {
      // Primary is Jabra, but Yealink is available and unpaired - offer it as fallback
      shouldOfferFallback = true;
      fallback = 'yealink';
    } else if (strategy === 'yealink' && hasJabra && !isJabraPaired) {
      // Primary is Yealink, but Jabra is available and unpaired - offer it as fallback
      shouldOfferFallback = true;
      fallback = 'jabra';
    }

    logger.info(
      `${MODULE_NAME} - detectOptimalPairingStrategy - Optimal strategy: ${strategy}, `
      + `shouldOfferFallback: ${shouldOfferFallback}, fallback: ${fallback}`,
    );

    return {
      primary: strategy,
      shouldOfferFallback,
      fallback,
    };
  } catch (error) {
    logger.warn(`${MODULE_NAME} - detectOptimalPairingStrategy - Error:`, error);
    return {
      primary: 'auto',
      shouldOfferFallback: false,
      fallback: null,
    };
  }
};

/**
 * Process devices to set redux and selected input output devices
 * @param appName
 * @param user
 * @returns {Promise<{inputDevices: *, outputDevice:
 * (*|null), inputDevice: (*|null), outputDevices: *}>}
 */
const setDevices = async (appName, user) => {
  const devices = await getMediaDevices();
  const inputDevice = getDefaultAudioInput(appName, user, devices);
  const outputDevice = getDefaultAudioOutput(appName, user, devices);
  const outputDeviceRinger = getDefaultAudioOutputRinger(appName, user, devices);
  const inputDevices = getFilteredAudioInput(devices);
  const outputDevices = getFilteredAudioOutput(devices);

  store.dispatch(setInputDevice(inputDevice?.deviceId));
  store.dispatch(setInputDevices(inputDevices));
  store.dispatch(setOutputDevice(outputDevice?.deviceId));
  store.dispatch(setOutputDeviceRinger(outputDeviceRinger?.deviceId));
  store.dispatch(setOutputDevices(outputDevices));
  updateElementSinkId();

  localStorage.setItem(`${appName}-${user}_inputAudioDevice`, inputDevice?.deviceId);
  localStorage.setItem(`${appName}-${user}_outputAudioDevice`, outputDevice?.deviceId);
  localStorage.setItem(`${appName}-${user}_outputAudioDeviceRinger`, outputDeviceRinger?.deviceId);

  logger.info(`${MODULE_NAME} - setDevices - devices`, devices);
  logger.info(`${MODULE_NAME} - setDevices - inputDevice`, inputDevice);
  logger.info(`${MODULE_NAME} - setDevices - outputDevice`, outputDevice);
  logger.info(`${MODULE_NAME} - setDevices - inputDevices`, inputDevices);
  logger.info(`${MODULE_NAME} - setDevices - outputDevices`, outputDevices);

  return {
    inputDevice,
    inputDevices,
    outputDevice,
    outputDevices,
  };
};

/**
 * handle device changes, this happens when there's a new device plugin or removed
 * Had to debounce this function,
 * when disconnecting or connecting a device there are actually two devices
 * being added and removed, one input and one output
 */
const handleDeviceChanges = _.debounce(async (appName, user) => {
  logger.info(`${MODULE_NAME} - handleDeviceChanges`);
  const iDevices = selectInputDevices(store.getState());
  const oDevices = selectOutputDevices(store.getState());

  const { inputDevices, outputDevices } = await setDevices(appName, user);

  const existingDevices = [...iDevices, ...oDevices];
  const newDevices = [...inputDevices, ...outputDevices];

  if ('hid' in navigator) {
    if (existingDevices.length !== newDevices.length) {
      if (newDevices.length < existingDevices.length) {
        const hidDevices = selectHidDevices(store.getState());

        if (hidDevices.length) {
          const callControl = hidDevices[0];

          const currentCallControl = selectHidDevice(store.getState());

          if (getName(currentCallControl) !== getName(callControl)) {
            const newInput = inputDevices.find((device) => device.kind === 'audioinput'
              && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));
            const newOutput = outputDevices.find((device) => device.kind === 'audiooutput'
              && device.label.toLowerCase().includes(getName(callControl).toLowerCase()));

            store.dispatch(setInputDevice(_.get(newInput, 'deviceId')));
            store.dispatch(setOutputDevice(_.get(newOutput, 'deviceId')));
            store.dispatch(setOutputDeviceRinger(_.get(newOutput, 'deviceId')));
            localStorage.setItem(`${appName}-${user}_inputAudioDevice`, _.get(newInput, 'deviceId'));
            localStorage.setItem(`${appName}-${user}_outputAudioDevice`, _.get(newOutput, 'deviceId'));
            localStorage.setItem(`${appName}-${user}_outputAudioDeviceRinger`, _.get(newOutput, 'deviceId'));
            updateElementSinkId();

            // Replace audio tracks for active calls with new input device
            if (newInput?.deviceId) {
              try {
                await replaceAudioTrackForActiveCalls(newInput.deviceId);
              } catch (error) {
                logger.error(`${MODULE_NAME} - Failed to replace audio tracks during device switch:`, error);
              }
            }

            setCallControl(callControl);
          }
        } else {
          store.dispatch(setHidDevice(null));
        }

        // Don't show consent dialog when devices are removed
      } else {
        // get the diff of the devices
        // this will return the input and output differences
        const diff = _.differenceBy(newDevices, existingDevices, 'deviceId');

        if (diff.length) {
          const deviceLabel = await getDeviceLabel(diff[0].deviceId);

          if (deviceLabel?.toLowerCase().includes('jabra') || deviceLabel?.toLowerCase().includes('yealink')) {
            // delay getting the headset device/call control
            // max tries is 10 seconds
            let headsetDevice;
            let triesLeft = 20;
            await new Promise((resolve) => {
              const interval = setInterval(() => {
                headsetDevice = getHeadsetDeviceByLabel(deviceLabel);
                if (headsetDevice || !triesLeft) {
                  resolve();
                  clearInterval(interval);
                }
                triesLeft -= 1;
              }, 200);
            }); // wait for headset device to be added

            // auto select new headset device
            if (headsetDevice) {
              const { device: callControl, type } = headsetDevice;
              const newInput = _.find(diff, { kind: 'audioinput' });
              const newOutput = _.find(diff, { kind: 'audiooutput' });

              store.dispatch(setInputDevice(_.get(newInput, 'deviceId')));
              store.dispatch(setOutputDevice(_.get(newOutput, 'deviceId')));
              store.dispatch(setOutputDeviceRinger(_.get(newOutput, 'deviceId')));
              localStorage.setItem(`${appName}-${user}_inputAudioDevice`, _.get(newInput, 'deviceId'));
              localStorage.setItem(`${appName}-${user}_outputAudioDevice`, _.get(newOutput, 'deviceId'));
              localStorage.setItem(`${appName}-${user}_outputAudioDeviceRinger`, _.get(newOutput, 'deviceId'));
              updateElementSinkId();

              // Replace audio tracks for active calls with new input device
              if (newInput?.deviceId) {
                try {
                  await replaceAudioTrackForActiveCalls(newInput.deviceId);
                } catch (error) {
                  logger.error(`${MODULE_NAME} - Failed to replace audio tracks during headset device switch:`, error);
                }
              }

              setHeadsetCallControl(callControl, type);

              logger.info(`${MODULE_NAME} - handleDeviceChanges - previous ${type} headset connected and paired`);
            } else {
              // Only show consent for newly added compatible devices that aren't already paired
              logger.info(`${MODULE_NAME} - handleDeviceChanges - new compatible device added but not automatically paired`
                + `, checking if consent needed for device: ${deviceLabel}`);
              await deviceNeedsConsent(diff[0].deviceId);
            }

            await updateDeviceLabels();
          }
        }
      }
    }
  }
}, 1000);

export const onboardDevices = async (appName, user, permission) => {
  try {
    const devices = await getMediaDevices();
    logger.info(`${MODULE_NAME} - onboardDevices - devices`, devices);

    if (permission) {
      const inputDevices = getFilteredAudioInput(devices);
      store.dispatch(setInputDevices(inputDevices));
      logger.info(`${MODULE_NAME} - onboardDevices - inputDevices`, inputDevices);
    }
    const outputDevices = getFilteredAudioOutput(devices);
    store.dispatch(setOutputDevices(outputDevices));
    logger.info(`${MODULE_NAME} - onboardDevices - outputDevices`, outputDevices);

    updateElementSinkId();
    await updateDeviceLabels();
  } catch (e) {
    logger.error(`${MODULE_NAME} - onboardDevices error`, e);
    bugsnagClient.notify(e);
    console.error(e);
  }
};

/**
 * Get browser devices and sync with HID devices
 * @param appName
 * @param user
 * @returns {Promise<void>}
 */
export const initDevices = async (appName, user) => {
  logger.info(`${MODULE_NAME} - initDevices`);

  try {
    navigator.mediaDevices.ondevicechange = () => handleDeviceChanges(appName, user);

    await setDevices(appName, user);

    if ('hid' in navigator) {
      const syncHid = async () => {
        await initHid();

        let triesLeft = 20;
        await new Promise((resolve) => {
          const interval = setInterval(() => {
            const hidDevices = selectHidDevices(store.getState());
            if (hidDevices?.length || !triesLeft) {
              clearInterval(interval);
              setTimeout(resolve, 500);
            }
            triesLeft -= 1;
          }, 200);
        });

        const hidDevices = selectHidDevices(store.getState());
        const deviceLabel = localStorage.getItem(`${appName}-${user}_hidDevice`);
        const callControl = getHidDeviceByLabel(deviceLabel);

        if (callControl) {
          setCallControl(callControl);
        } else if (hidDevices?.length && hidDevices[0]) {
          setCallControl(hidDevices[0]);
        }
      };

      const syncYealink = async () => {
        // Get previously authorized Yealink devices
        try {
          const hidDevices = await navigator.hid.getDevices();
          logger.info(`${MODULE_NAME} - syncYealink - found ${hidDevices.length} HID devices`);
          const yealinkDevices = hidDevices
            .filter((device) => device.vendorId === 0x6993)
            .map((device) => ({
              id: { equals: (other) => other.vendorId === device.vendorId && other.productId === device.productId },
              productName: device.productName,
              name: device.productName,
              vendorId: device.vendorId,
              productId: device.productId,
              isYealinkDevice: true,
              originalDevice: device,
            }));

          logger.info(`${MODULE_NAME} - syncYealink - found ${yealinkDevices.length} Yealink devices`, yealinkDevices.map((d) => d.name));

          if (yealinkDevices.length > 0) {
            // Process the devices through our standard flow
            await processSelectedHeadsetDevices('yealink', yealinkDevices);
          }

          // Wait a moment for state to update
          await new Promise((resolve) => {
            setTimeout(() => {
              const stateYealinkDevices = selectYealinkDevices(store.getState());
              const deviceLabel = localStorage.getItem(`${appName}-${user}_yealinkDevice`);
              const callControl = getYealinkDeviceByLabel(deviceLabel);

              if (callControl) {
                setYealinkDevice(callControl);
              } else if (stateYealinkDevices?.length && stateYealinkDevices[0]) {
                setYealinkDevice(stateYealinkDevices[0]);
              }
              resolve();
            }, 500);
          });
        } catch (error) {
          logger.error(`${MODULE_NAME} - syncYealink error:`, error);
        }
      };

      // Determine which headset type was previously active
      const previousJabraDevice = localStorage.getItem(`${appName}-${user}_hidDevice`);
      const previousYealinkDevice = localStorage.getItem(`${appName}-${user}_yealinkDevice`);
      const previousHeadsetType = localStorage.getItem(`${appName}-${user}_headsetType`);

      // Run both sync functions but prioritize based on what was previously set
      // Both must complete before checking consent to ensure all managers are initialized
      if (previousHeadsetType === 'yealink' && previousYealinkDevice) {
        // Yealink was the last active headset type
        await syncYealink();
        await syncHid();
      } else if (previousHeadsetType === 'jabra' && previousJabraDevice) {
        // Jabra was the last active headset type
        await syncHid();
        await syncYealink();
      } else if (previousYealinkDevice && !previousJabraDevice) {
        // Only Yealink device stored
        await syncYealink();
        await syncHid();
      } else if (previousJabraDevice && !previousYealinkDevice) {
        // Only Jabra device stored
        await syncHid();
        await syncYealink();
      } else {
        // Default case - run both with Jabra first (existing behavior)
        await syncHid();
        await syncYealink();
      }

      // After both headset managers are initialized, wait before checking consent
      // During onboarding, use a shorter delay (3s) to prompt user sooner
      // After onboarding, use longer delay (30s) for non-blocking startup
      const firstLogin = localStorage.getItem(`${appName}-${user}_onboarding`);
      const isOnboarding = firstLogin === ONBOARD_SETUP || firstLogin === null;
      const consentDelay = isOnboarding ? 1000 : 10000;

      logger.info(
        `${MODULE_NAME} - initDevices - waiting ${consentDelay / 1000}s before checking device consent `
        + `(onboarding: ${isOnboarding})`,
      );
      setTimeout(async () => {
        logger.info(`${MODULE_NAME} - initDevices - checking device consent after ${consentDelay / 1000}s delay`);
        // Mark initial load as complete - future device additions should trigger consent checks
        updateHidState({ isInitialLoad: false });
        await devicesNeedConsent(false);
      }, consentDelay);

      await updateDeviceLabels();
    }
  } catch (e) {
    logger.error(`${MODULE_NAME} - initDevices error`, e);
    bugsnagClient.notify(e);
    console.error(e);
  }
};
